import {
  ApartmentOutlined,
  CheckCircleOutlined,
  DeploymentUnitOutlined,
  FileOutlined,
  FolderOutlined,
  LockOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import {
  FC,
  PointerEvent,
  WheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import playCourseDepthBackground from '../../assets/playcourse-depth-background.jpg';
import { aimanagerModule1Topics } from '../../pages/aimanager-module1-data';
import {
  TCourseStructure,
  TPlaylistContentItem,
} from '../../types/responses/video';
import { TCourseTreeNode, buildCourseTree } from '../../utils/course-structure';
import {
  getPlayCourseFilePath,
  getPlayCourseItemPath,
  getPlayCourseLessonPath,
  getPlayCourseModulePath,
  getPlayCourseModulesPath,
  getPlayCourseSubtopicFilePath,
  getPlayCourseSubtopicPath,
  getPlayCourseTopicPath,
} from '../../utils/paths';

type TProps = {
  courseStructure?: TCourseStructure | null;
  currentItemIndex?: number;
  items: TPlaylistContentItem[];
  mode?: 'fileNavigator' | 'full';
  playlistId: string;
};

type TCardStatus =
  | 'locked'
  | 'completed'
  | 'current'
  | 'default'
  | 'placeholder';

type TDiscMetrics = {
  cardHeight: number;
  cardWidth: number;
  isPortraitPhone: boolean;
  radius: number;
  sceneHeight: number;
  surfaceHeight: number;
  surfaceWidth: number;
};

const CARD_RATIO = 260 / 190;
const CARD_GAP_RATIO = 0.2;
const PANEL_SLOT_EDGE_INSET_RATIO = 1.5 + CARD_GAP_RATIO;
const DISC_PERSPECTIVE = 2200;
const DISC_NEUTRAL_ANGLE = 90;
const DISC_TILT_RANGE = 16;
const DEFAULT_TILT = DISC_NEUTRAL_ANGLE;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const SLOT_ANGLE = 26;
const SEAM_PASS_SPEED = 0.08;
const SEAM_RESISTANCE_FRICTION = 0.82;
const SEAM_RESISTANCE_MIN = 0.42;
const SEAM_SNAP_RANGE = SLOT_ANGLE * 0.38;
const VISIBLE_SIDE_SLOTS = 3;
const VISIBLE_EDGE_ANGLE = SLOT_ANGLE * VISIBLE_SIDE_SLOTS;
const MOBILE_HIGH_DETAIL_EDGE_ANGLE = SLOT_ANGLE * 1.35;
const WHEEL_ROTATION_SCALE = 0.7;
const INERTIA_STOP_SPEED = 0.012;
const MAX_TILT = DISC_NEUTRAL_ANGLE + DISC_TILT_RANGE;
const MIN_TILT = DISC_NEUTRAL_ANGLE - DISC_TILT_RANGE;
const TILT_DEGREES_PER_PIXEL = 0.08;
const TILT_GESTURE_THRESHOLD = 8;
const MIN_DISC_SLOTS = 12;
const DESKTOP_MENU_WIDTH = 140;
const TREE_HOVER_WIDTH = 980;
const TREE_CONTENT_SCALE = 2 / 3;
const SCROLL_EDGE_RATIO = 0.22;
const SCROLL_EDGE_MIN = 54;
const SCROLL_EDGE_MAX = 120;
const SCROLL_MAX_SPEED = 8 / 3;
const SCROLL_MANUAL_LOCK_MS = 900;
const TOUCH_SCROLL_CLICK_THRESHOLD = 6;
const TOUCH_SCROLL_MAX_VELOCITY = 1.8;
const TOUCH_SCROLL_STOP_VELOCITY = 0.025;
const TOUCH_SCROLL_FRICTION = 0.92;
const TESSERACT_GESTURE_THRESHOLD = 14;
const TESSERACT_ITEM_STEP_PX = 58;
const TESSERACT_LEVEL_STEP_PX = 64;
const TESSERACT_FIGURE_SIZE_RATIO = 0.9;
const TESSERACT_FIGURE_MAX_SIZE = 680;
const TESSERACT_MOBILE_FIGURE_SIZE_RATIO = 0.9;
const TESSERACT_MOBILE_FIGURE_MAX_SIZE = 420;
const TESSERACT_FACE_HIT_PADDING = 4;
const TESSERACT_VERTEX_HIT_RATIO = 0.045;
const TESSERACT_VERTEX_HIT_MIN = 10;
const TESSERACT_VERTEX_HIT_MAX = 20;
const TESSERACT_3D_HANDLE_VERTEX_COUNT = 4;
const TESSERACT_OUTER_CUBE_SCALE = 1;
const TESSERACT_INNER_CUBE_SCALE = 0.5;
const TESSERACT_4D_CAMERA_DISTANCE =
  (TESSERACT_OUTER_CUBE_SCALE + TESSERACT_INNER_CUBE_SCALE) /
  (TESSERACT_OUTER_CUBE_SCALE - TESSERACT_INNER_CUBE_SCALE);
const TESSERACT_4D_SCALE_NUMERATOR =
  TESSERACT_OUTER_CUBE_SCALE * (TESSERACT_4D_CAMERA_DISTANCE - 1);
const TESSERACT_PROJECTION_SCALE = 24;
const TESSERACT_DEPTH_PERSPECTIVE = 0.12;
const TESSERACT_DEFAULT_FREE_ROTATION_X = -24;
const TESSERACT_DEFAULT_FREE_ROTATION_Y = 34;
const TESSERACT_DEFAULT_ROTATION_DEGREES_PER_MS = 0.0018;

type TDragMode = 'pending' | 'rotate' | 'tilt';
type TCardDetailLevel = 'focused' | 'near' | 'far';
type TNodeMetadata = Pick<TCourseTreeNode, 'description' | 'benefit'>;
type TScrollDragState = {
  lastX: number;
  lastTime: number;
  lastY: number;
  moved: boolean;
  pointerId: number;
  startScrollLeft: number;
  startScrollTop: number;
  startX: number;
  startY: number;
  target: HTMLElement;
  velocityX: number;
  velocityY: number;
} | null;
type TTesseractActiveMode = 'idle' | 'items' | 'levels' | 'free';
type TTesseractPlaneMode = Extract<TTesseractActiveMode, 'items' | 'levels'>;
type TTesseractHandleKind = 'face4d' | 'vertex3d';
type TTesseractDragState = {
  faceKey?: string;
  freeStartX: number;
  freeStartY: number;
  handleKind: TTesseractHandleKind;
  lastStep: number;
  lastX: number;
  lastY: number;
  mode: TTesseractActiveMode;
  previewItem: number;
  previewLevel: number;
  pointerId: number;
  startX: number;
  startY: number;
} | null;
type TTesseractVertex = readonly [number, number, number, number];
type TProjectedTesseractPoint = {
  depth: number;
  x: number;
  y: number;
};
type TTesseractPreview = {
  item: number;
  level: number;
};
type TTesseractRotation = TTesseractPreview;
type TTesseractFreeRotation = {
  x: number;
  y: number;
};
type TTesseractAxis = 0 | 1 | 2 | 3;
type TTesseractFace = {
  indexes: number[];
  key: string;
  mode: TTesseractPlaneMode;
};
type TProjectedTesseractFace = {
  center: TProjectedTesseractPoint;
  face: TTesseractFace;
  isFocused: boolean;
  points: TProjectedTesseractPoint[];
};
type TTesseractFigurePoint = {
  x: number;
  y: number;
};

const TESSERACT_VERTICES: TTesseractVertex[] = [-1, 1].flatMap((x) =>
  [-1, 1].flatMap((y) =>
    [-1, 1].flatMap((z) => [-1, 1].map((w) => [x, y, z, w] as const))
  )
);
const TESSERACT_EDGES = TESSERACT_VERTICES.flatMap((vertex, index) =>
  TESSERACT_VERTICES.map((other, otherIndex) => {
    const differenceCount = vertex.reduce(
      (count, value, axis) => count + (value === other[axis] ? 0 : 1),
      0
    );

    return differenceCount === 1 && otherIndex > index
      ? ([index, otherIndex] as const)
      : null;
  }).filter((edge): edge is readonly [number, number] => Boolean(edge))
);
const TESSERACT_FACE_AXES = [-1, 1] as const;

const getTesseractFaceIndexes = (
  fixedAxes: Partial<Record<TTesseractAxis, -1 | 1>>
): number[] =>
  TESSERACT_VERTICES.reduce<number[]>((indexes, vertex, index) => {
    const belongsToFace = ([0, 1, 2, 3] as const).every((axis) => {
      const fixedValue = fixedAxes[axis];
      return fixedValue === undefined || vertex[axis] === fixedValue;
    });

    if (belongsToFace) indexes.push(index);
    return indexes;
  }, []);

const TESSERACT_ITEM_FACES: TTesseractFace[] = TESSERACT_FACE_AXES.flatMap(
  (y) =>
    TESSERACT_FACE_AXES.map((z) => ({
      indexes: getTesseractFaceIndexes({ 1: y, 2: z }),
      key: `item-${y}-${z}`,
      mode: 'items' as const,
    }))
);
const TESSERACT_LEVEL_FACES: TTesseractFace[] = TESSERACT_FACE_AXES.flatMap(
  (x) =>
    TESSERACT_FACE_AXES.map((z) => ({
      indexes: getTesseractFaceIndexes({ 0: x, 2: z }),
      key: `level-${x}-${z}`,
      mode: 'levels' as const,
    }))
);
const TESSERACT_DEPTH_FACES: TTesseractFace[] = TESSERACT_FACE_AXES.flatMap(
  (x) =>
    TESSERACT_FACE_AXES.map((y) => ({
      indexes: getTesseractFaceIndexes({ 0: x, 1: y }),
      key: `depth-${x}-${y}`,
      mode: 'items' as const,
    }))
);
const TESSERACT_CONNECTOR_FACES = [
  ...TESSERACT_ITEM_FACES,
  ...TESSERACT_LEVEL_FACES,
  ...TESSERACT_DEPTH_FACES,
];
const TESSERACT_OUTER_FRONT_FACE: TTesseractFace = {
  indexes: getTesseractFaceIndexes({ 2: 1, 3: 1 }),
  key: 'outer-front',
  mode: 'items',
};
const TESSERACT_OUTER_RIGHT_FACE: TTesseractFace = {
  indexes: getTesseractFaceIndexes({ 0: 1, 3: 1 }),
  key: 'outer-right',
  mode: 'levels',
};
const TESSERACT_SELECTABLE_HANDLE_FACES = [
  TESSERACT_OUTER_FRONT_FACE,
  TESSERACT_OUTER_RIGHT_FACE,
];

const getTesseractFacesForMode = (
  mode: TTesseractPlaneMode
): TTesseractFace[] =>
  mode === 'levels' ? TESSERACT_LEVEL_FACES : TESSERACT_ITEM_FACES;

const getTesseractVisualPlaneMode = (
  activeMode: TTesseractActiveMode
): TTesseractPlaneMode => (activeMode === 'levels' ? 'levels' : 'items');

const getTesseractSelectableFaceByKey = (key?: string): TTesseractFace =>
  TESSERACT_SELECTABLE_HANDLE_FACES.find((face) => face.key === key) ??
  TESSERACT_OUTER_FRONT_FACE;

const isCarouselDebugEnabled = () =>
  typeof window !== 'undefined' &&
  window.localStorage.debugPlayCourseCarousel === 'true';

const logCarouselDebug = (
  eventName: string,
  payload: Record<string, unknown>
) => {
  if (!isCarouselDebugEnabled()) return;
  console.debug(`[PlayCourseCarousel] ${eventName}`, payload);
};

const getCardStatus = (node: TCourseTreeNode): TCardStatus => {
  if (node.type === 'placeholder') return 'placeholder';
  if (node.type === 'material' && node.locked) return 'locked';
  if (node.containsCurrent) return 'current';
  if (node.isFullyCompleted) return 'completed';
  return 'default';
};

const getAvailabilityText = (status: TCardStatus): string => {
  if (status === 'placeholder') return 'Virtual slot';
  if (status === 'completed') return 'Completed · Available';
  if (status === 'current') return 'Current';
  if (status === 'locked') {
    return 'Locked until previous material is completed';
  }
  return 'Available';
};

const getStatusCaption = (status: TCardStatus): string => {
  if (status === 'placeholder') return 'Not available';
  if (status === 'completed') return 'Completed';
  if (status === 'current') return 'Current';
  if (status === 'locked') return 'Locked';
  return '';
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const normalizeMetadataKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeBenefit = (benefit?: string[] | string | null): string[] => {
  if (Array.isArray(benefit)) return benefit.filter(Boolean);
  if (typeof benefit === 'string' && benefit.trim()) return [benefit.trim()];
  return [];
};

// Side cards render precomputed bar primitives instead of the focused
// card's text sections: solid segments sized by word length imitate
// heavily blurred text while costing a handful of untextured rects
// instead of a per-frame blur() rasterization.
const BAR_BASE_WIDTH = 16;
const BAR_CHAR_WIDTH = 7;
const BAR_MAX_WIDTH = 96;
const MAX_TITLE_BARS = 14;
const MAX_CONTENT_BAR_ROWS = 8;
const MAX_BARS_PER_ROW = 4;
const MAX_DESCRIPTION_BAR_WORDS = 32;
const MAX_BENEFIT_BAR_ROWS = 5;
const MAX_BENEFIT_BAR_WORDS = 6;

const computeWordBars = (text: string, maxWords: number): number[] =>
  text
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, maxWords)
    .map((word) =>
      Math.min(
        BAR_BASE_WIDTH + (word.length - 1) * BAR_CHAR_WIDTH,
        BAR_MAX_WIDTH
      )
    );

const computeTitleBars = (title: string): number[] =>
  computeWordBars(title, MAX_TITLE_BARS);

type TSideBars = {
  benefits: number[][];
  description: number[];
  materials: number[][];
};

// Bars mirror the focused card's real sections, keeping their colors:
// Description/Benefit paragraphs become accent-green segments, and the
// contents list (files of a lesson, lessons of a topic) becomes dotted
// rows in the list's own tone. Titles stay as real text.
const computeSideBars = (node: TCourseTreeNode): TSideBars => {
  const metadata = getModule1Metadata(node);
  const description = metadata.description
    ? computeWordBars(metadata.description, MAX_DESCRIPTION_BAR_WORDS)
    : [];
  const benefits = normalizeBenefit(metadata.benefit)
    .slice(0, MAX_BENEFIT_BAR_ROWS)
    .map((benefit) => computeWordBars(benefit, MAX_BENEFIT_BAR_WORDS));
  const materials = node.children.length
    ? node.children
        .slice(0, MAX_CONTENT_BAR_ROWS)
        .map((child) =>
          computeTitleBars(child.title).slice(0, MAX_BARS_PER_ROW)
        )
    : node.type === 'material'
      ? [computeTitleBars(node.title).slice(0, MAX_BARS_PER_ROW)]
      : [];

  return { benefits, description, materials };
};

const getNodeTopicNumber = (node: TCourseTreeNode): string | null => {
  const source = `${node.key} ${node.title}`;
  const match = source.match(/topic[\s-]*(\d+(?:\.\d+)*)/i);
  return match?.[1] ?? null;
};

const MODULE1_LESSON_METADATA_BY_SOURCE = new Map<string, TNodeMetadata>();
const MODULE1_LESSON_METADATA_BY_TOPIC = new Map<
  string,
  Array<{ key: string; metadata: TNodeMetadata }>
>();

aimanagerModule1Topics.forEach((topic) => {
  const topicLessons: Array<{ key: string; metadata: TNodeMetadata }> = [];

  topic.materials.forEach((material) => {
    const metadata = {
      benefit: material.benefit,
      description: material.description,
    };
    const nameKey = normalizeMetadataKey(material.name);

    MODULE1_LESSON_METADATA_BY_SOURCE.set(
      `${topic.number}:${material.sourceLessonId}`,
      metadata
    );

    if (nameKey) {
      topicLessons.push({ key: nameKey, metadata });
    }
  });

  MODULE1_LESSON_METADATA_BY_TOPIC.set(topic.number, topicLessons);
});

const getModule1Metadata = (node: TCourseTreeNode): TNodeMetadata => {
  if (node.type === 'placeholder') return {};

  if (node.description || node.benefit) {
    return {
      benefit: node.benefit,
      description: node.description,
    };
  }

  if (node.type === 'material') return {};
  if (node.type === 'topic') return {};

  const topicNumber = getNodeTopicNumber(node);
  if (!topicNumber) return {};

  const sourceLessonMatch = node.key.match(/lesson-(\d+)/i);
  const sourceLessonId = sourceLessonMatch
    ? Number(sourceLessonMatch[1])
    : null;
  const sourceMetadata = sourceLessonId
    ? MODULE1_LESSON_METADATA_BY_SOURCE.get(`${topicNumber}:${sourceLessonId}`)
    : undefined;

  if (sourceMetadata) return sourceMetadata;

  const normalizedKey = normalizeMetadataKey(`${node.key} ${node.title}`);
  const nameMetadata = MODULE1_LESSON_METADATA_BY_TOPIC.get(topicNumber)?.find(
    (item) => normalizedKey.includes(item.key)
  )?.metadata;

  return nameMetadata ?? {};
};

const getCycleAngle = (itemCount: number, angleStep: number): number =>
  itemCount > 1 ? itemCount * angleStep : 0;

const getLoopedAngle = (angle: number, cycleAngle: number): number => {
  if (cycleAngle <= 0) return angle;
  return angle - Math.round(angle / cycleAngle) * cycleAngle;
};

const rotatePair = (
  first: number,
  second: number,
  angleRad: number
): readonly [number, number] => {
  const sin = Math.sin(angleRad);
  const cos = Math.cos(angleRad);

  return [first * cos - second * sin, first * sin + second * cos];
};

const normalizeTesseractAngle = (angle: number): number =>
  ((angle % 360) + 360) % 360;

const projectTesseractVertex = (
  vertex: TTesseractVertex,
  itemAngle: number,
  levelAngle: number,
  freeRotation: TTesseractFreeRotation
): TProjectedTesseractPoint => {
  let [x, y, z, w] = vertex;

  [x, w] = rotatePair(x, w, itemAngle * DEG_TO_RAD);
  [y, w] = rotatePair(y, w, levelAngle * DEG_TO_RAD);

  const schlegelScale =
    TESSERACT_4D_SCALE_NUMERATOR / (TESSERACT_4D_CAMERA_DISTANCE - w);
  x *= schlegelScale;
  y *= schlegelScale;
  z *= schlegelScale;

  [x, z] = rotatePair(x, z, freeRotation.y * DEG_TO_RAD);
  [y, z] = rotatePair(y, z, freeRotation.x * DEG_TO_RAD);

  const perspectiveScale = clamp(
    1 + z * TESSERACT_DEPTH_PERSPECTIVE,
    0.78,
    1.22
  );

  return {
    depth: z + w * 0.18,
    x: 50 + x * TESSERACT_PROJECTION_SCALE * perspectiveScale,
    y: 50 + y * TESSERACT_PROJECTION_SCALE * perspectiveScale,
  };
};

const getProjectedTesseractPoints = (
  itemAngle: number,
  levelAngle: number,
  freeRotation: TTesseractFreeRotation
): TProjectedTesseractPoint[] => {
  const points = TESSERACT_VERTICES.map((vertex) =>
    projectTesseractVertex(vertex, itemAngle, levelAngle, freeRotation)
  );
  const center = points.reduce(
    (acc, point) => ({
      maxX: Math.max(acc.maxX, point.x),
      maxY: Math.max(acc.maxY, point.y),
      minX: Math.min(acc.minX, point.x),
      minY: Math.min(acc.minY, point.y),
    }),
    {
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
    }
  );
  const offsetX = 50 - (center.minX + center.maxX) / 2;
  const offsetY = 50 - (center.minY + center.maxY) / 2;

  return points.map((point) => ({
    ...point,
    x: point.x + offsetX,
    y: point.y + offsetY,
  }));
};

const sortPlanePoints = (
  points: TProjectedTesseractPoint[],
  indexes: number[]
): TProjectedTesseractPoint[] => {
  const selected = indexes.map((index) => points[index]);
  const center = selected.reduce(
    (acc, point) => ({
      x: acc.x + point.x / selected.length,
      y: acc.y + point.y / selected.length,
    }),
    { x: 0, y: 0 }
  );

  return selected.sort(
    (first, second) =>
      Math.atan2(first.y - center.y, first.x - center.x) -
      Math.atan2(second.y - center.y, second.x - center.x)
  );
};

const getPolygonCenter = (
  points: TProjectedTesseractPoint[]
): TProjectedTesseractPoint =>
  points.reduce(
    (acc, point) => ({
      depth: acc.depth + point.depth / points.length,
      x: acc.x + point.x / points.length,
      y: acc.y + point.y / points.length,
    }),
    { depth: 0, x: 0, y: 0 }
  );

const isPointInPolygon = (
  point: TTesseractFigurePoint,
  polygon: TProjectedTesseractPoint[]
): boolean => {
  let inside = false;

  for (
    let index = 0, previousIndex = polygon.length - 1;
    index < polygon.length;
    previousIndex = index++
  ) {
    const current = polygon[index];
    const previous = polygon[previousIndex];
    const crossesY =
      current.y > point.y !== previous.y > point.y &&
      point.x <
        ((previous.x - current.x) * (point.y - current.y)) /
          (previous.y - current.y || Number.EPSILON) +
          current.x;

    if (crossesY) inside = !inside;
  }

  return inside;
};

const getDistanceToSegment = (
  point: TTesseractFigurePoint,
  start: TTesseractFigurePoint,
  end: TTesseractFigurePoint
): number => {
  const lengthSquared =
    (end.x - start.x) * (end.x - start.x) +
    (end.y - start.y) * (end.y - start.y);

  if (lengthSquared === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }

  const progress = clamp(
    ((point.x - start.x) * (end.x - start.x) +
      (point.y - start.y) * (end.y - start.y)) /
      lengthSquared,
    0,
    1
  );
  const projection = {
    x: start.x + (end.x - start.x) * progress,
    y: start.y + (end.y - start.y) * progress,
  };

  return Math.hypot(point.x - projection.x, point.y - projection.y);
};

const getDistanceToPolygon = (
  point: TTesseractFigurePoint,
  polygon: TProjectedTesseractPoint[]
): number =>
  polygon.reduce((distance, current, index) => {
    const previous = polygon[(index + polygon.length - 1) % polygon.length];
    return Math.min(distance, getDistanceToSegment(point, previous, current));
  }, Number.POSITIVE_INFINITY);

const isMobileTesseractSlot = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 860px) and (orientation: portrait)').matches;

const getTesseractFigureSize = (rect: DOMRect): number => {
  const mobileSlot = isMobileTesseractSlot();
  const sizeRatio = mobileSlot
    ? TESSERACT_MOBILE_FIGURE_SIZE_RATIO
    : TESSERACT_FIGURE_SIZE_RATIO;
  const maxSize = mobileSlot
    ? TESSERACT_MOBILE_FIGURE_MAX_SIZE
    : TESSERACT_FIGURE_MAX_SIZE;

  return Math.min(rect.width * sizeRatio, rect.height * sizeRatio, maxSize);
};

const getPointerInTesseractFigure = (
  event: PointerEvent<HTMLElement>,
  target: HTMLElement
): TTesseractFigurePoint => {
  const rect = target.getBoundingClientRect();
  const figureSize = getTesseractFigureSize(rect);
  const figureLeft = rect.left + (rect.width - figureSize) / 2;
  const figureTop = rect.top + (rect.height - figureSize) / 2;

  return {
    x: ((event.clientX - figureLeft) / figureSize) * 100,
    y: ((event.clientY - figureTop) / figureSize) * 100,
  };
};

const projectTesseractFaces = (
  points: TProjectedTesseractPoint[],
  faces: TTesseractFace[],
  focusedFace?: TTesseractFace
): TProjectedTesseractFace[] =>
  faces
    .map((face) => {
      const facePoints = sortPlanePoints(points, face.indexes);
      const center = getPolygonCenter(facePoints);

      return {
        center,
        face,
        isFocused: face.key === focusedFace?.key,
        points: facePoints,
      };
    })
    .sort((first, second) => first.center.depth - second.center.depth);

const getNearestEquivalentRotation = (
  targetRotation: number,
  currentRotation: number,
  cycleAngle: number
): number => {
  if (cycleAngle <= 0) return targetRotation;
  return (
    targetRotation +
    Math.round((currentRotation - targetRotation) / cycleAngle) * cycleAngle
  );
};

const getNearestSeamAnchor = (
  rotation: number,
  itemCount: number,
  angleStep: number
): { distance: number; index: number; rotation: number } | null => {
  if (itemCount <= 1 || angleStep <= 0) return null;

  const cycleAngle = getCycleAngle(itemCount, angleStep);
  const firstRotation = getNearestEquivalentRotation(0, rotation, cycleAngle);
  const lastRotation = getNearestEquivalentRotation(
    -(itemCount - 1) * angleStep,
    rotation,
    cycleAngle
  );
  const firstDistance = Math.abs(rotation - firstRotation);
  const lastDistance = Math.abs(rotation - lastRotation);

  return firstDistance <= lastDistance
    ? { distance: firstDistance, index: 0, rotation: firstRotation }
    : { distance: lastDistance, index: itemCount - 1, rotation: lastRotation };
};

const getSeamResistance = (
  rotation: number,
  itemCount: number,
  angleStep: number
): number => {
  const anchor = getNearestSeamAnchor(rotation, itemCount, angleStep);
  if (!anchor || anchor.distance >= SEAM_SNAP_RANGE) return 1;

  return clamp(
    SEAM_RESISTANCE_MIN +
      (1 - SEAM_RESISTANCE_MIN) * (anchor.distance / SEAM_SNAP_RANGE),
    SEAM_RESISTANCE_MIN,
    1
  );
};

const getViewportWidth = (): number =>
  document.documentElement.clientWidth ||
  window.innerWidth ||
  document.body.clientWidth;

const getViewportHeight = (): number =>
  document.documentElement.clientHeight ||
  window.innerHeight ||
  document.body.clientHeight;

const getDiscMetrics = (width: number, height: number): TDiscMetrics => {
  const isCompact = width <= 720;
  const isPortraitPhone = isCompact && height >= width;
  const sceneHeight = clamp(
    height - (isPortraitPhone ? 132 : isCompact ? 220 : 175),
    height * (isPortraitPhone ? 0.76 : isCompact ? 0.62 : 0.72),
    height * (isPortraitPhone ? 0.94 : 0.86)
  );
  const widthBasedCardWidth = isPortraitPhone
    ? clamp(width * 0.88, 280, 520)
    : isCompact
      ? clamp(width * 0.68, 190, 330)
      : clamp(width * 0.32, 360, 640);
  const maxCardHeight = sceneHeight * 0.9;
  const cardWidth = isPortraitPhone
    ? widthBasedCardWidth
    : Math.min(widthBasedCardWidth, maxCardHeight / CARD_RATIO);
  const cardHeight = isPortraitPhone
    ? clamp(cardWidth * 0.58, 168, sceneHeight * 0.44)
    : cardWidth * CARD_RATIO;
  const cardPitch =
    (isPortraitPhone ? cardHeight : cardWidth) * (1 + CARD_GAP_RATIO);
  const radius = cardPitch / (2 * Math.sin((SLOT_ANGLE * Math.PI) / 360));
  const surfaceWidth = isPortraitPhone
    ? Math.min(height * 0.82, radius * 2 + cardHeight * 0.5)
    : Math.min(width * 1.35, radius * 2 + cardWidth * 0.6);
  const surfaceHeight = isPortraitPhone
    ? cardWidth * 0.5
    : cardHeight * (isCompact ? 0.54 : 0.62);

  return {
    cardHeight,
    cardWidth,
    isPortraitPhone,
    radius,
    sceneHeight,
    surfaceHeight,
    surfaceWidth,
  };
};

// Auto-focus = the study frontier ("актуальный доступный"): the node
// containing the current item (first not-completed available material in
// through-file order). When the frontier is outside this subtree: a fully
// studied level focuses its LAST real node, an untouched one its FIRST.
const getTargetFocusIndex = (nodes: TCourseTreeNode[]): number => {
  const currentLocalIndex = nodes.findIndex((node) => node.containsCurrent);
  if (currentLocalIndex >= 0) return currentLocalIndex;

  const realIndexes = nodes.reduce<number[]>((acc, node, index) => {
    if (node.type !== 'placeholder') acc.push(index);
    return acc;
  }, []);
  if (!realIndexes.length) return 0;

  const allCompleted = realIndexes.every(
    (index) => nodes[index].isFullyCompleted
  );
  return allCompleted ? realIndexes[realIndexes.length - 1] : realIndexes[0];
};

const findNodeByKey = (nodes: TCourseTreeNode[], key?: string) =>
  key ? nodes.find((node) => node.key === key) : undefined;

const findMaterialGroupPath = (
  nodes: TCourseTreeNode[],
  itemIndex: number,
  path: TCourseTreeNode[] = []
): TCourseTreeNode[] | null => {
  for (const node of nodes) {
    if (node.type === 'material' && node.itemRef?.index === itemIndex) {
      return path;
    }

    const result = findMaterialGroupPath(node.children, itemIndex, [
      ...path,
      node,
    ]);
    if (result) return result;
  }

  return null;
};

const areNodePathsEqual = (
  left: TCourseTreeNode[],
  right: TCourseTreeNode[]
): boolean =>
  left.length === right.length &&
  left.every((node, index) => node.key === right[index]?.key);

const isInteractiveElement = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  Boolean(
    target.closest(
      'a, button, input, textarea, select, [role="button"], [data-carousel-panel="true"]'
    )
  );

// A group node (lesson/subtopic) whose children are all materials. Its
// focused disc card previews those files as clickable "Materials" rows,
// so the lesson's contents are reachable before entering the files disc.
const isMaterialLeafGroup = (node?: TCourseTreeNode): boolean =>
  Boolean(
    node &&
      node.type !== 'material' &&
      node.type !== 'placeholder' &&
      node.children.length > 0 &&
      node.children.every((child) => child.type === 'material')
  );

const getPathUrl = (
  playlistId: string,
  nextPath: TCourseTreeNode[]
): string => {
  const [moduleNode, topicNode, lessonNode] = nextPath;
  const subtopicNode = nextPath[3];

  if (!moduleNode) {
    return getPlayCourseModulesPath(playlistId);
  }

  if (!topicNode) {
    return getPlayCourseModulePath(playlistId, moduleNode.key);
  }

  if (!lessonNode) {
    return getPlayCourseTopicPath(playlistId, moduleNode.key, topicNode.key);
  }

  if (!subtopicNode) {
    return getPlayCourseLessonPath(
      playlistId,
      moduleNode.key,
      topicNode.key,
      lessonNode.key
    );
  }

  return getPlayCourseSubtopicPath(
    playlistId,
    moduleNode.key,
    topicNode.key,
    lessonNode.key,
    subtopicNode.key
  );
};

const getMaterialUrl = (
  playlistId: string,
  parentPath: TCourseTreeNode[],
  itemIndex: number
): string => {
  const [moduleNode, topicNode, lessonNode, subtopicNode] = parentPath;

  if (moduleNode && topicNode && lessonNode && subtopicNode) {
    return getPlayCourseSubtopicFilePath(
      playlistId,
      moduleNode.key,
      topicNode.key,
      lessonNode.key,
      subtopicNode.key,
      itemIndex
    );
  }

  if (moduleNode && topicNode && lessonNode) {
    return getPlayCourseFilePath(
      playlistId,
      moduleNode.key,
      topicNode.key,
      lessonNode.key,
      itemIndex
    );
  }

  return getPlayCourseItemPath(playlistId, itemIndex);
};

const getTreeLevelLabel = (node: TCourseTreeNode): string => {
  if (node.type === 'module') return 'Module';
  if (node.type === 'topic') return 'Topic';
  if (node.type === 'lesson') return 'Lesson';
  if (node.type === 'subtopic') return 'Subtopic';
  if (node.type === 'material') return 'File';
  return 'Slot';
};

const createPlaceholderNode = (
  levelKey: string,
  index: number
): TCourseTreeNode => ({
  children: [],
  completedCount: 0,
  containsCurrent: false,
  isFullyCompleted: false,
  key: `placeholder-${levelKey}-${index}`,
  locked: true,
  title: 'Empty slot',
  totalCount: 0,
  type: 'placeholder',
});

// The ring is padded to 12 slots with virtual "Empty slot" cards, but the
// real nodes always occupy the leading slots side by side; placeholders
// only ever FOLLOW them (real lessons first, then empties).
const withVirtualSlots = (
  nodes: TCourseTreeNode[],
  levelKey: string
): TCourseTreeNode[] => {
  const slotCount = Math.max(nodes.length, MIN_DISC_SLOTS);
  const placeholderCount = slotCount - nodes.length;

  if (placeholderCount <= 0) return nodes;

  return [
    ...nodes,
    ...Array.from({ length: placeholderCount }, (_, index) =>
      createPlaceholderNode(levelKey, index + 1)
    ),
  ];
};

const Wrapper = styled('section', {
  shouldForwardProp: (prop) => prop !== '$navigatorOnly',
})<{ $navigatorOnly?: boolean }>`
  display: ${({ $navigatorOnly }) => ($navigatorOnly ? 'contents' : 'block')};
  margin-top: ${({ $navigatorOnly }) => ($navigatorOnly ? 0 : 12)}px;

  @media (max-width: 720px) and (orientation: portrait) {
    margin-top: 0;
    padding-top: 0;
  }
`;

const TopicHeader = styled('div')`
  display: flex;
  align-items: baseline;
  flex: 0 1 auto;
  gap: 10px;
  min-width: 0;

  @media (max-width: 720px) {
    align-items: center;
    gap: 8px;
  }
`;

const TopicTitle = styled('h3')`
  margin: 0;
  color: ${({ theme }) => theme.COLORS.WHITE._200};
  font-size: 17px;
  font-weight: 600;

  @media (max-width: 720px) {
    display: block;
    overflow: hidden;
    font-size: 16px;
    line-height: 1.24;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TopicProgress = styled('span')`
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 13px;
  white-space: nowrap;

  @media (max-width: 948px), (pointer: coarse) {
    font-size: 10px;
    font-weight: 400;
    opacity: 0.78;
  }
`;

const CarouselNavRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
  transition:
    opacity 0.22s ease,
    transform 0.34s cubic-bezier(0.2, 0.82, 0.18, 1);

  @media (max-width: 948px), (pointer: coarse) {
    max-height: 42px;

    main[data-mobile-chrome-visible='false'] & {
      opacity: 0;
      pointer-events: none;
      transform: translateY(-130%);
    }
  }

  @media (max-width: 720px) {
    gap: 8px;
  }
`;

const DiscViewport = styled('div', {
  shouldForwardProp: (prop) => prop !== '$sceneHeight',
})<{ $sceneHeight: number }>`
  position: relative;
  z-index: 2;
  overflow: hidden;
  height: ${({ $sceneHeight }) => $sceneHeight}px;
  margin: 8px -12px 0;
  perspective: ${DISC_PERSPECTIVE}px;
  touch-action: none;
  transition:
    height 0.45s ease,
    margin 0.45s ease;
  user-select: none;

  @media (max-width: 720px) {
    margin-inline: -8px;
    touch-action: pan-y;
  }

  @media (max-width: 720px) and (orientation: portrait) {
    margin: 6px -8px 0;
    perspective: 1700px;
    touch-action: none;
  }

  /* When the mobile chrome slides away the freed space belongs to the
     drum: the viewport grows symmetrically so the top/bottom edge slots
     (and their simplified neighbours) come into view, while the drum's
     vertical center stays exactly where it was. */
  @media (max-width: 860px) {
    main[data-mobile-chrome-visible='false'] & {
      height: calc(
        ${({ $sceneHeight }) => $sceneHeight}px +
          var(--play-course-mobile-chrome-reclaimed-height, 0px)
      );
      margin-top: calc(
        var(--play-course-mobile-chrome-reclaimed-height, 0px) / -2
      );
      margin-bottom: calc(
        var(--play-course-mobile-chrome-reclaimed-height, 0px) / -2
      );
    }
  }
`;

const CarouselFrame = styled('div', {
  shouldForwardProp: (prop) => prop !== '$navigatorOnly',
})<{ $navigatorOnly?: boolean }>`
  position: ${({ $navigatorOnly }) => ($navigatorOnly ? 'fixed' : 'relative')};
  inset: ${({ $navigatorOnly }) => ($navigatorOnly ? 0 : 'auto')};
  z-index: ${({ $navigatorOnly }) => ($navigatorOnly ? 4090 : 'auto')};
  pointer-events: ${({ $navigatorOnly }) => ($navigatorOnly ? 'none' : 'auto')};
`;

const SpatialBackdrop = styled('div')`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  background: linear-gradient(
      90deg,
      ${({ theme }) => theme.COLORS.BLACK._100}73 0%,
      ${({ theme }) => theme.COLORS.BLACK._100}3d 24%,
      ${({ theme }) => theme.COLORS.ACCENT._800}24 50%,
      ${({ theme }) => theme.COLORS.BLACK._100}3d 76%,
      ${({ theme }) => theme.COLORS.BLACK._100}78 100%
    ),
    linear-gradient(
      180deg,
      ${({ theme }) => theme.COLORS.BLACK._100}63 0%,
      ${({ theme }) => theme.COLORS.BLACK._100}24 42%,
      ${({ theme }) => theme.COLORS.BLACK._100}4f 100%
    ),
    radial-gradient(
      ellipse at 50% 48%,
      ${({ theme }) => theme.COLORS.ACCENT._200}38 0%,
      transparent 34%
    ),
    url(${playCourseDepthBackground});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  filter: hue-rotate(48deg) saturate(0.74) brightness(0.78) contrast(1.14);
  opacity: 0.94;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
        ellipse at 50% 48%,
        ${({ theme }) => theme.COLORS.ACCENT._200}0d 0%,
        transparent 36%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.COLORS.BLACK._100}33 0%,
        transparent 42%,
        ${({ theme }) => theme.COLORS.BLACK._100}3b 100%
      );
    mix-blend-mode: multiply;
  }

  @media (max-width: 860px) and (orientation: portrait) {
    background: linear-gradient(
        90deg,
        ${({ theme }) => theme.COLORS.BLACK._100}96 0%,
        ${({ theme }) => theme.COLORS.BLACK._100}52 24%,
        ${({ theme }) => theme.COLORS.ACCENT._800}1d 50%,
        ${({ theme }) => theme.COLORS.BLACK._100}52 76%,
        ${({ theme }) => theme.COLORS.BLACK._100}9c 100%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.COLORS.BLACK._100}78 0%,
        ${({ theme }) => theme.COLORS.BLACK._100}30 42%,
        ${({ theme }) => theme.COLORS.BLACK._100}64 100%
      ),
      radial-gradient(
        ellipse at 50% 48%,
        ${({ theme }) => theme.COLORS.ACCENT._200}2c 0%,
        transparent 34%
      ),
      url(${playCourseDepthBackground});
    filter: hue-rotate(48deg) saturate(0.7) brightness(0.68) contrast(1.16);
    opacity: 0.96;

    &::after {
      background: radial-gradient(
          ellipse at 50% 48%,
          ${({ theme }) => theme.COLORS.ACCENT._200}10 0%,
          transparent 36%
        ),
        linear-gradient(
          180deg,
          ${({ theme }) => theme.COLORS.BLACK._100}4a 0%,
          transparent 42%,
          ${({ theme }) => theme.COLORS.BLACK._100}55 100%
        );
    }
  }
`;

const TreeDockButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$slotIndex',
})<{ $active: boolean; $slotIndex: number }>`
  position: fixed;
  z-index: 5100;
  left: 0;
  top: ${({ $slotIndex }) => 290 + $slotIndex * 58}px;
  display: inline-flex;
  width: ${DESKTOP_MENU_WIDTH}px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-left: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.COLORS.ACCENT._200 : 'transparent'};
  background: ${({ $active, theme }) =>
    $active ? `${theme.COLORS.ACCENT._800}18` : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.COLORS.ACCENT._200 : theme.COLORS.BLACK._700};
  cursor: pointer;
  transition:
    background 0.22s ease,
    border-color 0.22s ease,
    color 0.22s ease,
    text-shadow 0.22s ease,
    transform 0.34s cubic-bezier(0.2, 0.82, 0.18, 1);

  span {
    font-size: 29px;
  }

  &:hover {
    color: ${({ theme }) => theme.COLORS.ACCENT._200};
    text-shadow: 0 0 16px ${({ theme }) => theme.COLORS.ACCENT._200}88;
  }

  @media (max-width: 948px), (pointer: coarse) {
    top: auto;
    bottom: 3px;
    left: calc(8px + ${({ $slotIndex }) => $slotIndex * 58}px);
    width: 54px;
    height: 62px;
    border: 0;
    border-radius: 0;
    background: ${({ $active, theme }) =>
      $active ? `${theme.COLORS.ACCENT._800}18` : 'transparent'};
    box-shadow: none;
    color: ${({ $active, theme }) =>
      $active ? theme.COLORS.ACCENT._200 : theme.COLORS.WHITE._100};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;

    span {
      font-size: 28px;
    }

    main[data-mobile-chrome-visible='false'] & {
      transform: translateY(120%);
    }
  }

  @media (max-width: 380px), (pointer: coarse) {
    left: calc(5px + ${({ $slotIndex }) => $slotIndex * 53}px);
    width: 50px;

    span {
      font-size: 25px;
    }
  }
`;

const StructureTree = styled('aside', {
  shouldForwardProp: (prop) =>
    prop !== '$cardHeight' && prop !== '$cardWidth' && prop !== '$visible',
})<{ $cardHeight: number; $cardWidth: number; $visible: boolean }>`
  position: absolute;
  z-index: 4200;
  top: 50%;
  left: calc(50% - ${({ $cardWidth }) => $cardWidth * 1.72}px);
  width: ${({ $cardWidth }) => $cardWidth}px;
  height: ${({ $cardHeight }) => $cardHeight}px;
  overflow: hidden;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.COLORS.ACCENT._100};
  border-radius: 14px;
  background: radial-gradient(
      circle at 52% 42%,
      ${({ theme }) => theme.COLORS.ACCENT._200}1a 0%,
      transparent 35%
    ),
    linear-gradient(
      145deg,
      ${({ theme }) => theme.COLORS.WHITE._100}18,
      ${({ theme }) => theme.COLORS.ACCENT._800}18 46%,
      ${({ theme }) => theme.COLORS.BLACK._400}8f
    );
  box-shadow:
    0 0 30px ${({ theme }) => theme.COLORS.ACCENT._100}4f,
    0 0 84px ${({ theme }) => theme.COLORS.ACCENT._100}24;
  backdrop-filter: blur(15px) saturate(1.15) contrast(0.9);
  -webkit-backdrop-filter: blur(15px) saturate(1.15) contrast(0.9);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transform: translateY(-50%)
    translateX(${({ $visible }) => ($visible ? '0' : 'calc(-100% - 180px)')});
  transition:
    opacity 0.84s ease,
    transform 1.26s cubic-bezier(0.2, 0.82, 0.18, 1);

  @media (max-width: 1180px) {
    left: 18px;
  }

  /* Mobile: the tree occupies the card-sized slot above the focused card.
     The slot inset is measured from the viewport edge, not the app chrome. */
  @media (max-width: 860px) and (orientation: portrait) {
    position: fixed;
    top: clamp(
      8px,
      calc(
        50dvh -
          ${({ $cardHeight }) => $cardHeight * PANEL_SLOT_EDGE_INSET_RATIO}px
      ),
      calc(100dvh - ${({ $cardHeight }) => $cardHeight}px - 8px)
    );
    right: auto;
    left: 50%;
    width: ${({ $cardWidth }) => $cardWidth}px;
    height: ${({ $cardHeight }) => $cardHeight}px;
    max-width: 94vw;
    max-height: calc(100dvh - 24px);
    border-width: 1px;
    border-radius: 14px;
    transform: translateX(-50%)
      translateY(${({ $visible }) => ($visible ? '0' : 'calc(-100dvh - 100%)')});
    transition:
      opacity 0.84s ease,
      transform 1.08s cubic-bezier(0.2, 0.82, 0.18, 1);
  }
`;

const TesseractPanel = styled('aside', {
  shouldForwardProp: (prop) =>
    prop !== '$activeMode' &&
    prop !== '$cardHeight' &&
    prop !== '$cardWidth' &&
    prop !== '$visible',
})<{
  $activeMode: TTesseractActiveMode;
  $cardHeight: number;
  $cardWidth: number;
  $visible: boolean;
}>`
  position: absolute;
  z-index: 4210;
  top: 50%;
  left: calc(50% + ${({ $cardWidth }) => $cardWidth * 0.72}px);
  display: grid;
  width: ${({ $cardWidth }) => $cardWidth}px;
  height: ${({ $cardHeight }) => $cardHeight}px;
  place-items: center;
  overflow: hidden;
  border: 1px solid
    ${({ $activeMode, theme }) =>
      $activeMode === 'idle'
        ? `${theme.COLORS.ACCENT._100}cc`
        : theme.COLORS.ACCENT._100};
  border-radius: 14px;
  background: radial-gradient(
      circle at 50% 42%,
      ${({ theme }) => theme.COLORS.ACCENT._200}22 0%,
      transparent 36%
    ),
    linear-gradient(
      145deg,
      ${({ theme }) => theme.COLORS.WHITE._100}16,
      ${({ theme }) => theme.COLORS.ACCENT._800}16 44%,
      ${({ theme }) => theme.COLORS.BLACK._400}8a
    );
  box-shadow:
    0 0 30px ${({ theme }) => theme.COLORS.ACCENT._100}4d,
    0 0 84px ${({ theme }) => theme.COLORS.ACCENT._100}22;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  cursor: grab;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  touch-action: none;
  transform: translateY(-50%)
    translateX(${({ $visible }) => ($visible ? '0' : 'calc(100% + 180px)')});
  transition:
    opacity 0.84s ease,
    transform 1.26s cubic-bezier(0.2, 0.82, 0.18, 1),
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  user-select: none;
  backdrop-filter: blur(15px) saturate(1.15) contrast(0.9);
  -webkit-backdrop-filter: blur(15px) saturate(1.15) contrast(0.9);

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 1180px) {
    right: 18px;
    left: auto;
  }

  /* Mobile: the manipulator occupies the card-sized slot below the focused
     card. The slot inset is measured from the viewport edge, not app chrome. */
  @media (max-width: 860px) and (orientation: portrait) {
    position: fixed;
    top: auto;
    right: auto;
    bottom: clamp(
      8px,
      calc(
        50dvh -
          ${({ $cardHeight }) => $cardHeight * PANEL_SLOT_EDGE_INSET_RATIO}px
      ),
      calc(100dvh - ${({ $cardHeight }) => $cardHeight}px - 8px)
    );
    left: 50%;
    width: ${({ $cardWidth }) => $cardWidth}px;
    height: ${({ $cardHeight }) => $cardHeight}px;
    max-width: 94vw;
    max-height: calc(100dvh - 24px);
    overflow: hidden;
    border-width: 1px;
    border-radius: 14px;
    transform: translateX(-50%)
      translateY(${({ $visible }) => ($visible ? '0' : 'calc(100dvh + 100%)')});
    transition:
      opacity 0.84s ease,
      transform 1.08s cubic-bezier(0.2, 0.82, 0.18, 1),
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }

  @media (max-width: 860px) and (orientation: landscape) {
    right: 14px;
    left: auto;
    width: ${({ $cardWidth }) => $cardWidth}px;
    height: ${({ $cardHeight }) => $cardHeight}px;
    max-width: 44vw;
    max-height: 88dvh;
    border-radius: 14px;
    transform: translateX(${({ $visible }) => ($visible ? '0' : '118%')});
    transition:
      opacity 0.84s ease,
      transform 1.08s cubic-bezier(0.2, 0.82, 0.18, 1),
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }
`;

const TesseractFigure = styled('svg')`
  display: block;
  width: min(
    ${TESSERACT_FIGURE_SIZE_RATIO * 100}%,
    ${TESSERACT_FIGURE_MAX_SIZE}px
  );
  height: min(
    ${TESSERACT_FIGURE_SIZE_RATIO * 100}%,
    ${TESSERACT_FIGURE_MAX_SIZE}px
  );
  overflow: hidden;
  color: inherit;
  filter: drop-shadow(0 0 12px ${({ theme }) => theme.COLORS.ACCENT._200}66);
  pointer-events: none;

  circle {
    fill: ${({ theme }) => theme.COLORS.ACCENT._200};
  }

  @media (max-width: 860px) and (orientation: portrait) {
    width: min(
      ${TESSERACT_MOBILE_FIGURE_SIZE_RATIO * 100}%,
      ${TESSERACT_MOBILE_FIGURE_MAX_SIZE}px
    );
    height: min(
      ${TESSERACT_MOBILE_FIGURE_SIZE_RATIO * 100}%,
      ${TESSERACT_MOBILE_FIGURE_MAX_SIZE}px
    );
    overflow: hidden;
  }
`;

const TesseractAxisHint = styled('div', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>`
  position: absolute;
  inset: 18px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? `${theme.COLORS.ACCENT._200}99`
        : `${theme.COLORS.WHITE._300}18`};
  border-radius: 10px;
  opacity: ${({ $active }) => ($active ? 1 : 0.58)};
  pointer-events: none;
  transform: rotate(45deg) scale(0.62);
`;

const TreeScrollArea = styled('div')`
  position: relative;
  z-index: 2;
  height: 100%;
  overflow: auto;
  padding: 26px 30px;
  cursor: grab;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.COLORS.ACCENT._800}55 transparent;
  touch-action: pan-x pan-y;
  user-select: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: ${({ theme }) => theme.COLORS.ACCENT._800}66;
  }

  @media (max-width: 720px) {
    padding: 18px 22px;
  }
`;

const TreeContent = styled('div')`
  width: ${100 / TREE_CONTENT_SCALE}%;
  min-height: ${100 / TREE_CONTENT_SCALE}%;
  transform: scale(${TREE_CONTENT_SCALE});
  transform-origin: top left;
`;

const TreeTitle = styled('div')`
  margin-bottom: 18px;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  text-shadow: 0 0 16px ${({ theme }) => theme.COLORS.ACCENT._200}66;
`;

const TreeList = styled('ol', {
  shouldForwardProp: (prop) => prop !== '$root',
})<{ $root?: boolean }>`
  position: relative;
  width: max-content;
  min-width: 100%;
  margin: 0;
  padding: 0 34px 0 ${({ $root }) => ($root ? 0 : 34)}px;
  list-style: none;
`;

const TreeBranch = styled('li')`
  position: relative;
  margin: 0;
  padding: 0 0 0 34px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 8px;
    width: 2px;
    background: ${({ theme }) => theme.COLORS.ACCENT._100};
    box-shadow:
      0 0 8px ${({ theme }) => theme.COLORS.ACCENT._100}99,
      0 0 18px ${({ theme }) => theme.COLORS.ACCENT._200}55;
    opacity: 0.9;
  }

  &:last-child::before {
    bottom: calc(100% - 22px);
  }
`;

const TreeNodeButton = styled('button', {
  shouldForwardProp: (prop) =>
    prop !== '$active' &&
    prop !== '$completed' &&
    prop !== '$focused' &&
    prop !== '$material' &&
    prop !== '$locked',
})<{
  $active: boolean;
  $completed: boolean;
  $focused: boolean;
  $material: boolean;
  $locked: boolean;
}>`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  column-gap: 12px;
  align-items: center;
  position: relative;
  width: 100%;
  min-width: max-content;
  min-height: 44px;
  margin: 0;
  padding: 6px 2px;
  border: 0;
  background: ${({ $completed, $material, theme }) =>
    $completed && $material ? `${theme.COLORS.ACCENT._800}14` : 'transparent'};
  border-radius: 8px;
  color: ${({ $active, $completed, $focused, theme }) =>
    $focused || $active
      ? theme.COLORS.ACCENT._200
      : $completed
        ? theme.COLORS.BLACK._700
        : theme.COLORS.BLACK._100};
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  font: inherit;
  text-align: left;
  opacity: ${({ $locked }) => ($locked ? 0.58 : 1)};
  box-shadow: ${({ $completed, $material, theme }) =>
    $completed && $material
      ? `inset 3px 0 0 ${theme.COLORS.ACCENT._200}55`
      : 'none'};
  text-shadow: ${({ $active, theme }) =>
    $active ? `0 0 18px ${theme.COLORS.ACCENT._200}88` : 'none'};

  &::before {
    content: '';
    position: absolute;
    top: 21px;
    left: -31px;
    width: 36px;
    height: 2px;
    background: ${({ $focused, theme }) =>
      $focused ? theme.COLORS.ACCENT._100 : `${theme.COLORS.ACCENT._200}dd`};
    box-shadow: ${({ $focused, theme }) =>
      $focused
        ? `0 0 14px ${theme.COLORS.ACCENT._100}`
        : `0 0 8px ${theme.COLORS.ACCENT._200}66`};
  }

  &:hover {
    color: ${({ $locked, theme }) =>
      $locked ? theme.COLORS.BLACK._700 : theme.COLORS.ACCENT._200};
  }
`;

const TreeDot = styled('span', {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$focused',
})<{ $active: boolean; $focused: boolean }>`
  width: ${({ $focused }) => ($focused ? 12 : 9)}px;
  height: ${({ $focused }) => ($focused ? 12 : 9)}px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.COLORS.ACCENT._100 : `${theme.COLORS.ACCENT._200}99`};
  border-radius: 50%;
  background: ${({ $focused, theme }) =>
    $focused ? theme.COLORS.ACCENT._100 : 'transparent'};
  box-shadow: ${({ $active, theme }) =>
    $active ? `0 0 18px ${theme.COLORS.ACCENT._100}` : 'none'};
`;

const TreeText = styled('span')`
  display: flex;
  min-width: max-content;
  flex-direction: column;
  gap: 3px;
`;

const TreeKind = styled('span')`
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
  text-transform: uppercase;
`;

const TreeName = styled('span')`
  overflow: visible;
  color: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.28;
  white-space: nowrap;
`;

const DiscScene = styled('div')`
  position: relative;
  height: 100%;
  transform-style: preserve-3d;
`;

const DiscSurface = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$cardHeight' &&
    prop !== '$isPortraitPhone' &&
    prop !== '$placement' &&
    prop !== '$surfaceHeight' &&
    prop !== '$tilt' &&
    prop !== '$surfaceWidth',
})<{
  $cardHeight: number;
  $isPortraitPhone: boolean;
  $placement: 'top' | 'bottom';
  $surfaceHeight: number;
  $surfaceWidth: number;
  $tilt: number;
}>`
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${({ $surfaceWidth }) => $surfaceWidth}px;
  height: ${({ $surfaceHeight }) => $surfaceHeight}px;
  border: 1px solid
    ${({ $placement, theme }) =>
      $placement === 'top'
        ? `${theme.COLORS.WHITE._600}33`
        : `${theme.COLORS.BLACK._800}88`};
  border-radius: 50%;
  background: ${({ $placement, theme }) =>
    $placement === 'top'
      ? `radial-gradient(ellipse at center, ${theme.COLORS.ACCENT._600_dark}12 0%, ${theme.COLORS.BLACK._400}22 46%, transparent 72%)`
      : `radial-gradient(ellipse at center, ${theme.COLORS.BLACK._100}3d 0%, ${theme.COLORS.BLACK._300}24 52%, transparent 76%)`};
  box-shadow: ${({ $placement, theme }) =>
    $placement === 'top'
      ? `0 0 44px ${theme.COLORS.ACCENT._800}30 inset`
      : `0 30px 70px ${theme.COLORS.BLACK._100}99`};
  pointer-events: none;
  transform: ${({ $cardHeight, $isPortraitPhone, $placement, $tilt }) =>
    $isPortraitPhone
      ? `translate(-50%, -50%) translateX(${
          $placement === 'top' ? -$cardHeight / 2 : $cardHeight / 2
        }px) rotateY(${86 - ($tilt - DISC_NEUTRAL_ANGLE) * 0.55}deg)`
      : `translate(-50%, -50%) translateY(${
          $placement === 'top' ? -$cardHeight / 2 : $cardHeight / 2
        }px) rotateX(${86 - ($tilt - DISC_NEUTRAL_ANGLE) * 0.55}deg)`};
`;

const DiscStage = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$isPortraitPhone' && prop !== '$radius' && prop !== '$tilt',
})<{ $isPortraitPhone: boolean; $radius: number; $tilt: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  transform: translate(-50%, -50%)
    ${({ $isPortraitPhone, $tilt }) =>
      $isPortraitPhone
        ? `rotateY(${($tilt - DISC_NEUTRAL_ANGLE) * 0.65}deg)`
        : `rotateX(${$tilt - DISC_NEUTRAL_ANGLE}deg)`}
    translateZ(${({ $radius }) => -$radius}px);
  transform-style: preserve-3d;
`;

const swapIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const CardBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: flex-start;
  position: relative;
  z-index: 2;
  height: 100%;
  padding: 40px 34px 24px;
  border-radius: inherit;
  animation: ${swapIn} 0.12s ease;
  transition: filter 0.25s ease;
  transform: translateZ(2px);

  @media (max-width: 720px) {
    gap: 10px;
    padding: 34px 22px 18px;
  }

  @media (max-width: 720px) and (orientation: portrait) {
    gap: 10px;
    padding: 34px 20px 18px;
  }
`;

const BarsBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  z-index: 2;
  height: 100%;
  padding: 40px 34px 24px;
  border-radius: inherit;
  animation: ${swapIn} 0.12s ease;
  transform: translateZ(2px);

  @media (max-width: 720px) {
    gap: 10px;
    padding: 34px 22px 18px;
  }
`;

const BarRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 7px 9px;
  align-items: center;
`;

const WordBar = styled('span', {
  shouldForwardProp: (prop) => prop !== '$width',
})<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  height: 13px;
  border-radius: 7px;
  background: ${({ theme }) => theme.COLORS.WHITE._300}59;
`;

const BarFiller = styled('div')`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  margin-top: 4px;
  overflow: hidden;
`;

const BarSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BarParagraph = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
`;

const AccentBar = styled(WordBar)`
  height: 10px;
  border-radius: 5px;
  background: ${({ theme }) => theme.COLORS.ACCENT._200}40;
`;

const LabelBar = styled('span')`
  width: 58px;
  height: 9px;
  border-radius: 5px;
  background: ${({ theme }) => theme.COLORS.ACCENT._100}59;
`;

const RowDot = styled('span')`
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border: 1px solid ${({ theme }) => theme.COLORS.WHITE._300}66;
  border-radius: 50%;
`;

const AccentDot = styled(RowDot)`
  border-color: ${({ theme }) => theme.COLORS.ACCENT._200}88;
`;

const CardHeader = styled('div')`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 8px 14px;
  min-height: 0;

  @media (max-width: 720px) and (orientation: portrait) {
    grid-template-columns: auto auto minmax(0, 1fr);
    gap: 6px 10px;
  }
`;

const LockOverlay = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.45);
  color: ${({ theme }) => theme.COLORS.WHITE._300};
  font-size: 54px;

  @media (max-width: 720px) {
    font-size: 36px;
  }
`;

const CheckBadge = styled('span')`
  position: absolute;
  top: 18px;
  right: 18px;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  font-size: 28px;

  @media (max-width: 720px) {
    top: 10px;
    right: 10px;
    font-size: 20px;
  }
`;

const KindIcon = styled('span')`
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  font-size: 34px;
  line-height: 1;

  @media (max-width: 720px) {
    font-size: 24px;
  }
`;

const OrderNo = styled('span')`
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 38px;
  font-weight: 700;
  line-height: 0.95;

  @media (max-width: 720px) {
    font-size: 25px;
  }
`;

const Name = styled('span')`
  display: block;
  position: relative;
  z-index: 2;
  overflow: hidden;
  color: ${({ theme }) => theme.COLORS.WHITE._300};
  font-size: 18px;
  font-weight: 600;
  line-height: 1.22;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 720px) {
    font-size: 14px;
  }

  @media (max-width: 720px) and (orientation: portrait) {
    font-size: 15px;
  }
`;

const CardDetails = styled('div')`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding-top: 6px;
  padding-right: 8px;
  padding-bottom: 2px;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  cursor: grab;
  font-size: 14px;
  line-height: 1.42;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.COLORS.ACCENT._800}66 transparent;
  text-shadow: 0 0 14px ${({ theme }) => theme.COLORS.ACCENT._200}30;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: ${({ theme }) => theme.COLORS.ACCENT._800}66;
  }

  @media (max-width: 720px) {
    font-size: 12px;
  }

  @media (max-width: 720px) and (orientation: portrait) {
    padding-top: 8px;
    padding-right: 4px;
  }
`;

const CardDetailBlock = styled('section')`
  & + & {
    margin-top: 14px;
  }
`;

const CardDetailLabel = styled('div')`
  width: 100%;
  margin-bottom: 9px;
  color: ${({ theme }) => theme.COLORS.ACCENT._100};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-align: center;
  text-transform: uppercase;

  @media (max-width: 720px) {
    margin-bottom: 8px;
    font-size: 10px;
  }
`;

const CardDetailText = styled('p')`
  margin: 0;
`;

const CardBenefitList = styled('ul')`
  display: grid;
  gap: 6px;
  margin: 0;
  padding-left: 18px;
`;

const CardMaterialList = styled('div')`
  display: grid;
  gap: 4px;
`;

const CardMaterialRow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$completed' && prop !== '$locked',
})<{ $completed: boolean; $locked: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 9px;
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.WHITE._100}08;
  color: ${({ $locked, theme }) =>
    $locked ? theme.COLORS.BLACK._700 : theme.COLORS.WHITE._200};
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${({ $locked, theme }) =>
      $locked
        ? `${theme.COLORS.WHITE._100}08`
        : `${theme.COLORS.ACCENT._800}33`};
    color: ${({ $locked, theme }) =>
      $locked ? theme.COLORS.BLACK._700 : theme.COLORS.ACCENT._200};
  }
`;

const CardMaterialIcon = styled('span', {
  shouldForwardProp: (prop) => prop !== '$completed',
})<{ $completed: boolean }>`
  color: ${({ $completed, theme }) =>
    $completed ? theme.COLORS.ACCENT._200 : theme.COLORS.WHITE._300};
  font-size: 15px;
  line-height: 1;
`;

const CardMaterialName = styled('span')`
  flex: 1;
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusCaption = styled('span')`
  position: absolute;
  top: 10px;
  left: 50%;
  z-index: 4;
  width: calc(100% - 68px);
  margin: 0;
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 10px;
  line-height: 1;
  overflow: hidden;
  pointer-events: none;
  text-align: center;
  text-overflow: ellipsis;
  transform: translateX(-50%);
  white-space: nowrap;

  @media (max-width: 720px) {
    top: 8px;
    width: calc(100% - 44px);
    font-size: 9px;
  }
`;

const SpineCard = styled('button', {
  shouldForwardProp: (prop) =>
    prop !== '$angle' &&
    prop !== '$cardHeight' &&
    prop !== '$cardWidth' &&
    prop !== '$detailLevel' &&
    prop !== '$focused' &&
    prop !== '$frontness' &&
    prop !== '$isPortraitPhone' &&
    prop !== '$radius' &&
    prop !== '$status' &&
    prop !== '$visible',
})<{
  $angle: number;
  $cardHeight: number;
  $cardWidth: number;
  $detailLevel: TCardDetailLevel;
  $focused: boolean;
  $frontness: number;
  $isPortraitPhone: boolean;
  $radius: number;
  $status: TCardStatus;
  $visible: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $cardWidth }) => $cardWidth}px;
  height: ${({ $cardHeight }) => $cardHeight}px;
  margin-top: ${({ $cardHeight }) => -$cardHeight / 2}px;
  margin-left: ${({ $cardWidth }) => -$cardWidth / 2}px;
  border-radius: 14px;
  overflow: hidden;
  padding: 0;
  background: ${({ $detailLevel, $focused, $isPortraitPhone, theme }) =>
    $focused || ($isPortraitPhone && $detailLevel === 'near')
      ? `linear-gradient(145deg, ${theme.COLORS.WHITE._100}18, ${theme.COLORS.ACCENT._800}18 46%, ${theme.COLORS.BLACK._400}8f)`
      : $isPortraitPhone && $detailLevel === 'far'
        ? `linear-gradient(145deg, ${theme.COLORS.WHITE._100}08, ${theme.COLORS.ACCENT._800}0c 42%, ${theme.COLORS.BLACK._400}62)`
        : `linear-gradient(145deg, ${theme.COLORS.WHITE._100}0f, ${theme.COLORS.ACCENT._800}12 42%, ${theme.COLORS.BLACK._400}78)`};
  border: ${({ $detailLevel, $isPortraitPhone }) =>
      $isPortraitPhone && $detailLevel !== 'far' ? 2 : 1}px
    solid
    ${({ $detailLevel, $focused, $isPortraitPhone, $status, theme }) =>
      $focused || $status === 'current'
        ? theme.COLORS.ACCENT._100
        : $isPortraitPhone && $detailLevel === 'near'
          ? `${theme.COLORS.ACCENT._200}cc`
          : $status === 'placeholder'
            ? `${theme.COLORS.BLACK._500}99`
            : $status === 'completed'
              ? theme.COLORS.ACCENT._200
              : theme.COLORS.BLACK._500};
  cursor: ${({ $status }) =>
    $status === 'locked' || $status === 'placeholder' ? 'default' : 'grab'};
  font: inherit;
  text-align: left;
  opacity: ${({ $detailLevel, $isPortraitPhone, $status, $visible }) =>
    !$visible
      ? 0.08
      : $status === 'placeholder'
        ? $isPortraitPhone && $detailLevel === 'far'
          ? 0.24
          : 0.38
        : $isPortraitPhone && $detailLevel === 'far'
          ? 0.62
          : 1};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  z-index: ${({ $focused, $frontness }) =>
    $focused ? 3000 : Math.round(1000 + $frontness * 900)};
  transform: ${({ $angle, $isPortraitPhone, $radius }) =>
    $isPortraitPhone
      ? `rotateX(${$angle}deg) translateZ(${$radius}px)`
      : `rotateY(${$angle}deg) translateZ(${$radius}px)`};
  backface-visibility: hidden;
  transform-style: preserve-3d;
  box-shadow: ${({
    $detailLevel,
    $focused,
    $isPortraitPhone,
    $status,
    theme,
  }) =>
    $status === 'placeholder'
      ? `0 16px 34px ${theme.COLORS.BLACK._100}44`
      : $focused || $status === 'current'
        ? `0 0 30px ${theme.COLORS.ACCENT._100}66, 0 0 84px ${theme.COLORS.ACCENT._100}33`
        : $isPortraitPhone && $detailLevel === 'near'
          ? `0 0 22px ${theme.COLORS.ACCENT._200}44, 0 16px 38px ${theme.COLORS.BLACK._100}5c`
          : $isPortraitPhone && $detailLevel === 'far'
            ? `0 10px 24px ${theme.COLORS.BLACK._100}44`
            : `0 18px 42px ${theme.COLORS.BLACK._100}66`};
  backdrop-filter: ${({ $detailLevel, $isPortraitPhone }) =>
    $isPortraitPhone && $detailLevel === 'far'
      ? 'none'
      : 'blur(8px) saturate(1.25)'};
  isolation: isolate;
  filter: ${({ $detailLevel, $frontness, $isPortraitPhone }) =>
    $frontness > 0.88
      ? 'none'
      : $isPortraitPhone && $detailLevel === 'far'
        ? `brightness(${0.46 + $frontness * 0.34}) saturate(0.82)`
        : `brightness(${0.62 + $frontness * 0.36})`};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease,
    opacity 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    background: radial-gradient(
        circle at 52% 42%,
        ${({ theme }) => theme.COLORS.ACCENT._200}1f 0%,
        transparent 34%
      ),
      linear-gradient(
        100deg,
        transparent 0%,
        ${({ theme }) => theme.COLORS.ACCENT._100}14 44%,
        transparent 64%
      );
    mix-blend-mode: screen;
    opacity: ${({ $detailLevel, $focused, $frontness, $isPortraitPhone }) =>
      $focused
        ? 0.95
        : $isPortraitPhone && $detailLevel === 'near'
          ? 0.72
          : $isPortraitPhone && $detailLevel === 'far'
            ? 0.22
            : 0.35 + $frontness * 0.35};
    pointer-events: none;
  }

  ${({ $status }) =>
    $status === 'locked'
      ? `
        ${CardBody} {
          filter: blur(5px) brightness(0.55);
        }
      `
      : ''}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (max-width: 720px) and (orientation: portrait) {
    border-radius: 10px;
  }
`;

const PillsRow = styled('div')`
  display: flex;
  flex: 1 1 auto;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 2px 8px;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    background: transparent;
  }
`;

const TopicPill = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$completed',
})<{ $active: boolean; $completed: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  border-radius: 999px;
  background: ${({ $active, theme }) =>
    $active ? theme.COLORS.ACCENT._200 : 'transparent'};
  color: ${({ $active, $completed, theme }) =>
    $active
      ? theme.COLORS.BLACK._100
      : $completed
        ? theme.COLORS.ACCENT._200
        : theme.COLORS.WHITE._300};
  font-size: 14px;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease;
`;

const PillCheck = styled(CheckCircleOutlined)`
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
`;

const TesseractManipulator: FC<{
  activeMode: TTesseractActiveMode;
  freeRotation: TTesseractFreeRotation;
  itemAngle: number;
  levelAngle: number;
  selectedFaceKey: string;
}> = ({ activeMode, freeRotation, itemAngle, levelAngle, selectedFaceKey }) => {
  const points = useMemo(
    () => getProjectedTesseractPoints(itemAngle, levelAngle, freeRotation),
    [freeRotation, itemAngle, levelAngle]
  );
  const selectedHandleFace = useMemo(
    () => getTesseractSelectableFaceByKey(selectedFaceKey),
    [selectedFaceKey]
  );
  const planeMode =
    activeMode === 'idle'
      ? selectedHandleFace.mode
      : getTesseractVisualPlaneMode(activeMode);
  const handleFace = selectedHandleFace;
  const connectorFaces = useMemo(
    () => projectTesseractFaces(points, TESSERACT_CONNECTOR_FACES),
    [points]
  );
  const projectedFaces = useMemo(() => {
    const faces = getTesseractFacesForMode(planeMode);
    return projectTesseractFaces(points, faces, handleFace);
  }, [handleFace, planeMode, points]);
  const projectedHandleFace = useMemo(
    () =>
      projectTesseractFaces(
        points,
        [selectedHandleFace],
        selectedHandleFace
      )[0],
    [points, selectedHandleFace]
  );
  const focusedFace =
    projectedFaces.find((face) => face.isFocused) ?? projectedHandleFace;

  return (
    <TesseractFigure aria-hidden="true" focusable="false" viewBox="0 0 100 100">
      <defs>
        <linearGradient id="tesseract-edge-glow" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
          <stop offset="52%" stopColor="currentColor" stopOpacity="0.94" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.34" />
        </linearGradient>
      </defs>
      {connectorFaces.map(({ face, points: facePoints }) => (
        <polygon
          key={`connector-${face.key}`}
          fill="#79d8ff"
          fillOpacity="0.035"
          points={facePoints.map((point) => `${point.x},${point.y}`).join(' ')}
          stroke="#9be8ff"
          strokeOpacity="0.14"
          strokeWidth="0.42"
        />
      ))}
      {projectedFaces.map(({ face, isFocused, points: facePoints }) => (
        <polygon
          key={face.key}
          fill="currentColor"
          fillOpacity={isFocused ? 0.26 : 0.08}
          points={facePoints.map((point) => `${point.x},${point.y}`).join(' ')}
          stroke="currentColor"
          strokeOpacity={isFocused ? 0.92 : 0.36}
          strokeWidth={isFocused ? 1.25 : 0.62}
        />
      ))}
      {projectedHandleFace && (
        <polygon
          fill="currentColor"
          fillOpacity={activeMode === 'idle' ? 0.31 : 0.18}
          points={projectedHandleFace.points
            .map((point) => `${point.x},${point.y}`)
            .join(' ')}
          stroke="currentColor"
          strokeOpacity={0.96}
          strokeWidth={1.45}
        />
      )}
      {TESSERACT_EDGES.map(([start, end]) => {
        const from = points[start];
        const to = points[end];
        const startW = TESSERACT_VERTICES[start][3];
        const endW = TESSERACT_VERTICES[end][3];
        const isOuterCubeEdge = startW > 0 && endW > 0;
        const isInnerCubeEdge = startW < 0 && endW < 0;
        const depth = (from.depth + to.depth) / 2;
        const opacity = clamp(
          0.32 +
            depth * 0.18 +
            (isOuterCubeEdge ? 0.14 : isInnerCubeEdge ? 0.08 : 0.12),
          0.16,
          0.96
        );
        const strokeWidth = clamp(
          1.08 +
            depth * 0.18 +
            (isOuterCubeEdge ? 0.34 : isInnerCubeEdge ? 0.08 : 0.12),
          0.68,
          2.05
        );

        return (
          <line
            key={`${start}-${end}`}
            x1={from.x}
            x2={to.x}
            y1={from.y}
            y2={to.y}
            stroke="url(#tesseract-edge-glow)"
            strokeLinecap="round"
            strokeOpacity={opacity}
            strokeWidth={
              activeMode === 'idle' ? strokeWidth : strokeWidth + 0.38
            }
          />
        );
      })}
      {points.map((point, index) => {
        const isOuterVertex = TESSERACT_VERTICES[index][3] > 0;

        return (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            opacity={clamp(
              0.38 + point.depth * 0.16 + (isOuterVertex ? 0.06 : -0.03),
              0.2,
              0.98
            )}
            r={clamp(
              1.25 + point.depth * 0.16 + (isOuterVertex ? 0.32 : -0.08),
              0.78,
              2.05
            )}
          />
        );
      })}
      {focusedFace && (
        <circle
          cx={focusedFace.center.x}
          cy={focusedFace.center.y}
          opacity="0.98"
          r={activeMode === 'idle' ? 2.7 : 3.35}
          stroke="currentColor"
          strokeOpacity="0.82"
          strokeWidth="0.82"
        />
      )}
    </TesseractFigure>
  );
};

export const CourseCarousel: FC<TProps> = ({
  courseStructure,
  currentItemIndex,
  items,
  mode = 'full',
  playlistId,
}) => {
  const navigate = useNavigate();
  const { lessonKey, moduleKey, subtopicKey, topicKey } = useParams<{
    lessonKey?: string;
    moduleKey?: string;
    subtopicKey?: string;
    topicKey?: string;
  }>();
  const tree = useMemo(
    () => buildCourseTree(items, courseStructure),
    [courseStructure, items]
  );
  const isFileNavigator = mode === 'fileNavigator';
  const allowImplicitPanelReveal = false;

  const urlPath = useMemo(() => {
    const moduleNode = findNodeByKey(tree, moduleKey);
    if (!moduleNode) return [];

    const topicNode = findNodeByKey(moduleNode.children, topicKey);
    if (!topicNode) return [moduleNode];

    const lessonNode = findNodeByKey(topicNode.children, lessonKey);
    if (!lessonNode) return [moduleNode, topicNode];

    const subtopicNode = findNodeByKey(lessonNode.children, subtopicKey);
    if (!subtopicNode) return [moduleNode, topicNode, lessonNode];

    return [moduleNode, topicNode, lessonNode, subtopicNode];
  }, [lessonKey, moduleKey, subtopicKey, topicKey, tree]);

  const currentGroupPath = useMemo(() => {
    if (currentItemIndex === undefined) return [];
    return findMaterialGroupPath(tree, currentItemIndex) || [];
  }, [tree, currentItemIndex]);

  const initialPath = useMemo(() => {
    if (isFileNavigator && urlPath.length === 0) {
      return currentGroupPath;
    }
    return urlPath;
  }, [urlPath, currentGroupPath, isFileNavigator]);

  const [path, setPath] = useState<TCourseTreeNode[]>(initialPath);
  const [armedNodeKey, setArmedNodeKey] = useState<string | null>(null);
  const [menuHoverTreeOpen, setMenuHoverTreeOpen] = useState(false);
  const [manipulatorHover, setManipulatorHover] = useState(false);
  const [manipulatorPinned, setManipulatorPinned] = useState(false);
  const [treeHover, setTreeHover] = useState(false);
  const [treePinned, setTreePinned] = useState(false);
  const [tesseractActiveMode, setTesseractActiveMode] =
    useState<TTesseractActiveMode>('idle');
  const [tesseractFreeRotation, setTesseractFreeRotation] =
    useState<TTesseractFreeRotation>({
      x: TESSERACT_DEFAULT_FREE_ROTATION_X,
      y: TESSERACT_DEFAULT_FREE_ROTATION_Y,
    });
  const [tesseractBaseRotation, setTesseractBaseRotation] =
    useState<TTesseractRotation>({
      item: 0,
      level: 0,
    });
  const [tesseractPreview, setTesseractPreview] = useState<TTesseractPreview>({
    item: 0,
    level: 0,
  });
  const [tesseractSelectedFaceKey, setTesseractSelectedFaceKey] = useState(
    TESSERACT_OUTER_FRONT_FACE.key
  );
  const [tesseractHolding, setTesseractHolding] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(getViewportHeight);
  const [viewportWidth, setViewportWidth] = useState(getViewportWidth);
  const menuHoverSuppressedRef = useRef(false);
  const pointerInMenuRef = useRef(false);
  const activeParent = path[path.length - 1];
  const activeNodes = activeParent?.children ?? tree;
  const displayNodes = useMemo(
    () => withVirtualSlots(activeNodes, activeParent?.key ?? 'modules'),
    [activeNodes, activeParent?.key]
  );
  // Precomputed once per level; rotation frames only look widths up.
  const sideBarsByKey = useMemo(
    () =>
      new Map(displayNodes.map((node) => [node.key, computeSideBars(node)])),
    [displayNodes]
  );

  useEffect(() => {
    // In fileNavigator mode the URL's lesson keys can point at a different
    // branch than the file actually being shown (stale/deep link). When
    // that happens, sync to the file's REAL group instead of the URL path,
    // so `displayNodes` contains the current file and the focus/navigate
    // effects below don't ping-pong between the mismatched levels.
    const shouldUseCurrentGroup =
      isFileNavigator &&
      Number.isInteger(currentItemIndex) &&
      currentGroupPath.length > 0 &&
      (urlPath.length === 0 || !areNodePathsEqual(currentGroupPath, urlPath));

    setPath(shouldUseCurrentGroup ? currentGroupPath : urlPath);
    setArmedNodeKey(null);
  }, [currentItemIndex, urlPath, currentGroupPath, isFileNavigator]);

  useEffect(() => {
    setArmedNodeKey(null);
  }, [courseStructure, items]);

  useEffect(() => {
    if (!allowImplicitPanelReveal) {
      pointerInMenuRef.current = false;
      menuHoverSuppressedRef.current = true;
      setMenuHoverTreeOpen(false);
      return undefined;
    }

    const handleTreePointerMove = (event: globalThis.PointerEvent) => {
      const isPortraitTouchLayout =
        window.innerWidth <= 948 && window.innerHeight >= window.innerWidth;
      const isInMenu = event.clientX <= DESKTOP_MENU_WIDTH;

      if (!isInMenu && pointerInMenuRef.current) {
        menuHoverSuppressedRef.current = false;
      }
      pointerInMenuRef.current = isInMenu;

      if (isPortraitTouchLayout) {
        setMenuHoverTreeOpen(false);
        return;
      }

      if (menuHoverSuppressedRef.current) {
        setMenuHoverTreeOpen(false);
        return;
      }

      setMenuHoverTreeOpen(
        isInMenu || (menuHoverTreeOpen && event.clientX <= TREE_HOVER_WIDTH)
      );
    };
    const handleTreePointerDown = (event: globalThis.PointerEvent) => {
      const isPortraitTouchLayout =
        window.innerWidth <= 948 && window.innerHeight >= window.innerWidth;

      if (isPortraitTouchLayout) return;

      if (
        event.pointerType !== 'mouse' &&
        event.clientX <= DESKTOP_MENU_WIDTH
      ) {
        menuHoverSuppressedRef.current = false;
        setMenuHoverTreeOpen(true);
      }
    };
    const handleTreePointerUp = (event: globalThis.PointerEvent) => {
      if (!treeHover && !manipulatorHover && event.clientX > TREE_HOVER_WIDTH) {
        setMenuHoverTreeOpen(false);
      }
    };
    const handlePointerLeave = () => {
      setMenuHoverTreeOpen(false);
    };

    window.addEventListener('pointermove', handleTreePointerMove);
    window.addEventListener('pointerdown', handleTreePointerDown);
    window.addEventListener('pointerup', handleTreePointerUp);
    window.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      window.removeEventListener('pointermove', handleTreePointerMove);
      window.removeEventListener('pointerdown', handleTreePointerDown);
      window.removeEventListener('pointerup', handleTreePointerUp);
      window.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [
    allowImplicitPanelReveal,
    manipulatorHover,
    manipulatorPinned,
    menuHoverTreeOpen,
    treeHover,
    treePinned,
  ]);

  useEffect(() => {
    let timeoutId: number | undefined;
    const handleResize = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        setViewportHeight(getViewportHeight());
        setViewportWidth(getViewportWidth());
      }, 80);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const itemCount = displayNodes.length;
  // The drum is virtual: the visible arc keeps a constant slot angle and
  // radius regardless of the element count — slots beyond the visible
  // window simply live in the invisible part of the ring (not rendered).
  const angleStep = itemCount > 1 ? SLOT_ANGLE : 0;
  const metrics = useMemo(
    () => getDiscMetrics(viewportWidth, viewportHeight),
    [viewportHeight, viewportWidth]
  );
  const isMobilePanelLayout = metrics.isPortraitPhone;
  const dragModeRef = useRef<TDragMode>('pending');
  const inertiaFrameRef = useRef<number | null>(null);
  const lastMoveTimeRef = useRef(0);
  const lastMoveXRef = useRef(0);
  const lastMoveYRef = useRef(0);
  const initialRotation = useMemo(() => {
    const initialItemFocusIndex =
      Number.isInteger(currentItemIndex) && isFileNavigator
        ? displayNodes.findIndex(
            (node) => node.itemRef?.index === currentItemIndex
          )
        : -1;
    const focusIndex = initialItemFocusIndex >= 0 ? initialItemFocusIndex : 0;
    const initialAngleStep = displayNodes.length > 1 ? SLOT_ANGLE : 0;
    return -focusIndex * initialAngleStep;
  }, [currentItemIndex, isFileNavigator, displayNodes]);

  const rotationRef = useRef(initialRotation);
  const startTiltRef = useRef(DEFAULT_TILT);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const suppressClickRef = useRef(false);
  const scrollAutoFrameRef = useRef<number | null>(null);
  const scrollAutoTargetRef = useRef<HTMLElement | null>(null);
  const scrollAutoVelocityRef = useRef(0);
  const scrollDragRef = useRef<TScrollDragState>(null);
  const scrollInertiaFrameRef = useRef<number | null>(null);
  const scrollManualLockUntilRef = useRef(0);
  const lastDetailTapRef = useRef<{
    key: string;
    time: number;
  } | null>(null);
  const manipulatorWasVisibleRef = useRef(false);
  const tesseractDragRef = useRef<TTesseractDragState>(null);
  const tesseractAutoFrameRef = useRef<number | null>(null);
  const tesseractLastAutoFrameAtRef = useRef<number | null>(null);
  const tiltAllowedRef = useRef(false);
  const tiltRef = useRef(DEFAULT_TILT);
  const treePanelRef = useRef<HTMLElement | null>(null);
  const treeScrollRef = useRef<HTMLDivElement | null>(null);
  const totalDragRef = useRef(0);
  const velocityRef = useRef(0);
  const pendingFocusKeyRef = useRef<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rotation, setRotation] = useState(initialRotation);
  const [tilt, setTilt] = useState(DEFAULT_TILT);
  const cycleAngle = getCycleAngle(itemCount, angleStep);

  const stopTesseractAutoRotation = useCallback(() => {
    if (tesseractAutoFrameRef.current !== null) {
      cancelAnimationFrame(tesseractAutoFrameRef.current);
      tesseractAutoFrameRef.current = null;
    }
    tesseractLastAutoFrameAtRef.current = null;
  }, []);

  const stopAutoScroll = useCallback(() => {
    scrollAutoVelocityRef.current = 0;
    scrollAutoTargetRef.current = null;
    if (scrollAutoFrameRef.current !== null) {
      cancelAnimationFrame(scrollAutoFrameRef.current);
      scrollAutoFrameRef.current = null;
    }
  }, []);

  const stopScrollInertia = useCallback(() => {
    if (scrollInertiaFrameRef.current !== null) {
      cancelAnimationFrame(scrollInertiaFrameRef.current);
      scrollInertiaFrameRef.current = null;
    }
  }, []);

  const startScrollInertia = useCallback(
    (target: HTMLElement, velocityX: number, velocityY: number) => {
      stopScrollInertia();

      let activeVelocityX = clamp(
        velocityX,
        -TOUCH_SCROLL_MAX_VELOCITY,
        TOUCH_SCROLL_MAX_VELOCITY
      );
      let activeVelocityY = clamp(
        velocityY,
        -TOUCH_SCROLL_MAX_VELOCITY,
        TOUCH_SCROLL_MAX_VELOCITY
      );
      let lastTime = performance.now();

      const tick = (time: number) => {
        const elapsed = Math.min(time - lastTime, 34);
        lastTime = time;

        if (
          Math.abs(activeVelocityX) < TOUCH_SCROLL_STOP_VELOCITY &&
          Math.abs(activeVelocityY) < TOUCH_SCROLL_STOP_VELOCITY
        ) {
          stopScrollInertia();
          return;
        }

        const previousScrollLeft = target.scrollLeft;
        const previousScrollTop = target.scrollTop;
        target.scrollLeft += activeVelocityX * elapsed;
        target.scrollTop += activeVelocityY * elapsed;

        const maxScrollLeft = target.scrollWidth - target.clientWidth;
        const maxScrollTop = target.scrollHeight - target.clientHeight;
        const hitHorizontalBoundary =
          maxScrollLeft <= 0 ||
          (target.scrollLeft <= 0 && activeVelocityX < 0) ||
          (target.scrollLeft >= maxScrollLeft && activeVelocityX > 0) ||
          target.scrollLeft === previousScrollLeft;
        const hitVerticalBoundary =
          maxScrollTop <= 0 ||
          (target.scrollTop <= 0 && activeVelocityY < 0) ||
          (target.scrollTop >= maxScrollTop && activeVelocityY > 0) ||
          target.scrollTop === previousScrollTop;

        if (hitHorizontalBoundary) {
          activeVelocityX = 0;
        }
        if (hitVerticalBoundary) {
          activeVelocityY = 0;
        }

        if (hitHorizontalBoundary && hitVerticalBoundary) {
          stopScrollInertia();
          return;
        }

        const friction = Math.pow(TOUCH_SCROLL_FRICTION, elapsed / 16.67);
        activeVelocityX *= friction;
        activeVelocityY *= friction;
        scrollInertiaFrameRef.current = requestAnimationFrame(tick);
      };

      scrollInertiaFrameRef.current = requestAnimationFrame(tick);
    },
    [stopScrollInertia]
  );

  const setAutoScrollVelocity = useCallback(
    (target: HTMLElement | null, velocity: number) => {
      if (!target || Math.abs(velocity) < 0.1) {
        stopAutoScroll();
        return;
      }

      scrollAutoTargetRef.current = target;
      scrollAutoVelocityRef.current = velocity;

      if (scrollAutoFrameRef.current !== null) return;

      const tick = () => {
        const activeTarget = scrollAutoTargetRef.current;
        const speed = scrollAutoVelocityRef.current;

        if (!activeTarget || Math.abs(speed) < 0.1) {
          stopAutoScroll();
          return;
        }

        activeTarget.scrollTop += speed;
        scrollAutoFrameRef.current = requestAnimationFrame(tick);
      };

      scrollAutoFrameRef.current = requestAnimationFrame(tick);
    },
    [stopAutoScroll]
  );

  const updateAutoScrollFromPointer = useCallback(
    (target: HTMLElement | null, pointerY: number) => {
      if (!target) return;
      if (performance.now() < scrollManualLockUntilRef.current) {
        stopAutoScroll();
        return;
      }

      const rect = target.getBoundingClientRect();
      const edgeSize = clamp(
        rect.height * SCROLL_EDGE_RATIO,
        SCROLL_EDGE_MIN,
        SCROLL_EDGE_MAX
      );
      let velocity = 0;

      if (pointerY > rect.bottom) {
        velocity =
          clamp((pointerY - rect.bottom) / edgeSize, 0, 1.8) * SCROLL_MAX_SPEED;
      } else if (pointerY > rect.bottom - edgeSize) {
        velocity =
          clamp((pointerY - (rect.bottom - edgeSize)) / edgeSize, 0, 1) *
          SCROLL_MAX_SPEED;
      } else if (pointerY < rect.top) {
        velocity =
          -clamp((rect.top - pointerY) / edgeSize, 0, 1.8) * SCROLL_MAX_SPEED;
      } else if (pointerY < rect.top + edgeSize) {
        velocity =
          -clamp((rect.top + edgeSize - pointerY) / edgeSize, 0, 1) *
          SCROLL_MAX_SPEED;
      }

      setAutoScrollVelocity(target, velocity);
    },
    [setAutoScrollVelocity]
  );

  const handleScrollablePointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const target = event.target instanceof Element ? event.target : null;

      if (target?.closest('[data-tree-node-button="true"]')) {
        stopAutoScroll();
        stopScrollInertia();
        return;
      }

      scrollManualLockUntilRef.current =
        performance.now() + SCROLL_MANUAL_LOCK_MS;
      stopAutoScroll();
      stopScrollInertia();
      scrollDragRef.current = {
        lastX: event.clientX,
        lastTime: performance.now(),
        lastY: event.clientY,
        moved: false,
        pointerId: event.pointerId,
        startScrollLeft: event.currentTarget.scrollLeft,
        startScrollTop: event.currentTarget.scrollTop,
        startX: event.clientX,
        startY: event.clientY,
        target: event.currentTarget,
        velocityX: 0,
        velocityY: 0,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      event.stopPropagation();
    },
    [stopAutoScroll, stopScrollInertia]
  );

  const handleScrollablePointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const dragState = scrollDragRef.current;

      if (dragState?.pointerId === event.pointerId) {
        const time = performance.now();
        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        const frameDeltaX = event.clientX - dragState.lastX;
        const frameDeltaY = event.clientY - dragState.lastY;
        const elapsed = Math.max(time - dragState.lastTime, 8);

        if (Math.hypot(deltaX, deltaY) > TOUCH_SCROLL_CLICK_THRESHOLD) {
          dragState.moved = true;
        }

        dragState.velocityX = clamp(
          -frameDeltaX / elapsed,
          -TOUCH_SCROLL_MAX_VELOCITY,
          TOUCH_SCROLL_MAX_VELOCITY
        );
        dragState.velocityY = clamp(
          -frameDeltaY / elapsed,
          -TOUCH_SCROLL_MAX_VELOCITY,
          TOUCH_SCROLL_MAX_VELOCITY
        );
        dragState.lastX = event.clientX;
        dragState.lastY = event.clientY;
        dragState.lastTime = time;
        dragState.target.scrollLeft = dragState.startScrollLeft - deltaX;
        dragState.target.scrollTop = dragState.startScrollTop - deltaY;

        if (dragState.moved) {
          event.preventDefault();
        }
        event.stopPropagation();
        return;
      }

      updateAutoScrollFromPointer(event.currentTarget, event.clientY);
    },
    [updateAutoScrollFromPointer]
  );

  const handleScrollablePointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const dragState = scrollDragRef.current;

      if (dragState?.pointerId === event.pointerId) {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (dragState.moved) {
          suppressClickRef.current = true;
          window.setTimeout(() => {
            suppressClickRef.current = false;
          }, 120);
          startScrollInertia(
            dragState.target,
            dragState.velocityX,
            dragState.velocityY
          );
        } else {
          stopAutoScroll();
        }
        scrollManualLockUntilRef.current =
          performance.now() + SCROLL_MANUAL_LOCK_MS;
        scrollDragRef.current = null;
        event.stopPropagation();
      }
    },
    [startScrollInertia]
  );

  const setRotationValue = useCallback(
    (nextRotation: number) => {
      const normalized = getLoopedAngle(nextRotation, cycleAngle);
      rotationRef.current = normalized;
      setRotation(normalized);
    },
    [cycleAngle]
  );

  const setTiltValue = useCallback((nextTilt: number) => {
    const clamped = clamp(nextTilt, MIN_TILT, MAX_TILT);
    tiltRef.current = clamped;
    setTilt(clamped);
  }, []);

  const stopInertia = useCallback(() => {
    if (inertiaFrameRef.current !== null) {
      cancelAnimationFrame(inertiaFrameRef.current);
      inertiaFrameRef.current = null;
    }
  }, []);

  const settleFrameRef = useRef<number | null>(null);
  const wheelSettleTimerRef = useRef<number | null>(null);

  const stopSettle = useCallback(() => {
    if (settleFrameRef.current !== null) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = null;
    }
    if (wheelSettleTimerRef.current !== null) {
      window.clearTimeout(wheelSettleTimerRef.current);
      wheelSettleTimerRef.current = null;
    }
  }, []);

  // Drum detents: whenever free motion ends, ease the ring onto the
  // nearest slot anchor so the only stable positions are the slots
  // themselves (no in-between resting points).
  const settleToNearestSlot = useCallback(() => {
    if (angleStep <= 0) return;
    stopSettle();

    const target = Math.round(rotationRef.current / angleStep) * angleStep;
    const tick = () => {
      const diff = target - rotationRef.current;
      if (Math.abs(diff) < 0.05) {
        setRotationValue(target);
        settleFrameRef.current = null;
        return;
      }
      setRotationValue(rotationRef.current + diff * 0.18);
      settleFrameRef.current = requestAnimationFrame(tick);
    };

    settleFrameRef.current = requestAnimationFrame(tick);
  }, [angleStep, setRotationValue, stopSettle]);

  useEffect(
    () => () => {
      stopSettle();
    },
    [stopSettle]
  );

  const startInertia = useCallback(() => {
    stopInertia();

    const tick = () => {
      const speed = velocityRef.current;
      const seamAnchor = getNearestSeamAnchor(
        rotationRef.current,
        itemCount,
        angleStep
      );

      if (
        seamAnchor &&
        seamAnchor.distance < SEAM_SNAP_RANGE &&
        Math.abs(speed) < SEAM_PASS_SPEED
      ) {
        setRotationValue(seamAnchor.rotation);
        velocityRef.current = 0;
        inertiaFrameRef.current = null;
        return;
      }

      if (Math.abs(speed) < INERTIA_STOP_SPEED) {
        velocityRef.current = 0;
        inertiaFrameRef.current = null;
        settleToNearestSlot();
        return;
      }

      setRotationValue(rotationRef.current + speed * 16);
      velocityRef.current =
        speed *
        (seamAnchor && seamAnchor.distance < SEAM_SNAP_RANGE
          ? SEAM_RESISTANCE_FRICTION
          : 0.94);
      inertiaFrameRef.current = requestAnimationFrame(tick);
    };

    inertiaFrameRef.current = requestAnimationFrame(tick);
  }, [
    angleStep,
    itemCount,
    setRotationValue,
    settleToNearestSlot,
    stopInertia,
  ]);

  const prevPathKeysRef = useRef<string[]>([]);

  // Whether the file the player is actually showing right now is a child of
  // the currently displayed level. -1 when `path` doesn't (yet) match the
  // file's real group — e.g. a deep link whose lesson keys point at a
  // different branch, before the path-sync effect above catches up.
  const currentItemFocusIndex = useMemo(
    () =>
      Number.isInteger(currentItemIndex) && isFileNavigator
        ? displayNodes.findIndex(
            (node) => node.itemRef?.index === currentItemIndex
          )
        : -1,
    [currentItemIndex, displayNodes, isFileNavigator]
  );

  useEffect(() => {
    const pathKeys = path.map((node) => node.key);
    const prevKeys = prevPathKeysRef.current;
    // Ascending (new path is a prefix of the old one): focus the element
    // we just came out of instead of the study frontier.
    const isAscent =
      prevKeys.length > pathKeys.length &&
      pathKeys.every((key, index) => prevKeys[index] === key);
    const cameFromIndex = isAscent
      ? displayNodes.findIndex((node) => node.key === prevKeys[pathKeys.length])
      : -1;
    const pendingFocusKey = pendingFocusKeyRef.current;
    const pendingFocusIndex = pendingFocusKey
      ? displayNodes.findIndex((node) => node.key === pendingFocusKey)
      : -1;
    const focusIndex =
      pendingFocusIndex >= 0
        ? pendingFocusIndex
        : cameFromIndex >= 0
          ? cameFromIndex
          : currentItemFocusIndex >= 0
            ? currentItemFocusIndex
            : getTargetFocusIndex(displayNodes);
    pendingFocusKeyRef.current = null;
    prevPathKeysRef.current = pathKeys;
    stopInertia();
    stopSettle();
    setRotationValue(-focusIndex * angleStep);
    setTiltValue(DEFAULT_TILT);
  }, [
    angleStep,
    currentItemFocusIndex,
    displayNodes,
    path,
    setRotationValue,
    setTiltValue,
    stopInertia,
    stopSettle,
  ]);

  useEffect(
    () => () => {
      stopInertia();
    },
    [stopInertia]
  );

  useEffect(
    () => () => {
      stopAutoScroll();
    },
    [stopAutoScroll]
  );

  const focusedIndex = useMemo(() => {
    if (itemCount === 0) return 0;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    displayNodes.forEach((_, localIndex) => {
      const angle = getLoopedAngle(
        localIndex * angleStep + rotation,
        cycleAngle
      );
      const distance = Math.abs(angle);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = localIndex;
      }
    });

    return bestIndex;
  }, [displayNodes, angleStep, cycleAngle, itemCount, rotation]);
  const focusedNode = displayNodes[focusedIndex];
  const focusedNodeKey =
    focusedNode?.type !== 'placeholder' ? focusedNode?.key : undefined;
  useEffect(() => {
    stopAutoScroll();
  }, [focusedNodeKey, stopAutoScroll]);
  useEffect(() => {
    if (!isFileNavigator) return;
    // `path`/`displayNodes` don't (yet) match the current file's real group
    // — the path-sync effect is about to re-sync `path`. Focus here is just
    // the study-frontier fallback, not a real user pick; navigating off of
    // it would redirect to the wrong file and ping-pong against the reload.
    if (Number.isInteger(currentItemIndex) && currentItemFocusIndex === -1) {
      return;
    }
    if (focusedNode?.type !== 'material' || !focusedNode.itemRef) return;
    if (focusedNode.locked || focusedNode.itemRef.item.locked) return;

    const nextItemIndex = focusedNode.itemRef.index;
    if (nextItemIndex === currentItemIndex) return;

    navigate(
      getMaterialUrl(
        playlistId,
        findMaterialGroupPath(tree, nextItemIndex) ?? path,
        nextItemIndex
      )
    );
  }, [
    currentItemFocusIndex,
    currentItemIndex,
    focusedNode,
    isFileNavigator,
    navigate,
    path,
    playlistId,
    tree,
  ]);
  const activePathKeys = useMemo(
    () => new Set(path.map((node) => node.key)),
    [path]
  );

  const rotateToIndex = (localIndex: number) => {
    stopInertia();
    stopSettle();
    setRotationValue(
      getNearestEquivalentRotation(
        -localIndex * angleStep,
        rotationRef.current,
        cycleAngle
      )
    );
  };

  const focusNodeFromTree = (
    node: TCourseTreeNode,
    parentPath: TCourseTreeNode[]
  ) => {
    if (node.type === 'placeholder') return;
    if (node.type === 'material' && node.itemRef?.item.locked) return;

    setArmedNodeKey(null);

    if (isFileNavigator) {
      if (node.type !== 'material' || !node.itemRef) return;
      if (node.locked || node.itemRef.item.locked) return;

      const nextItemIndex = node.itemRef.index;
      if (nextItemIndex === currentItemIndex) return;

      if (areNodePathsEqual(path, parentPath)) {
        const localIndex = displayNodes.findIndex(
          (item) => item.key === node.key
        );

        if (localIndex >= 0) {
          rotateToIndex(localIndex);
          return;
        }
      }

      navigate(getMaterialUrl(playlistId, parentPath, nextItemIndex));
      return;
    }

    if (areNodePathsEqual(path, parentPath)) {
      const localIndex = displayNodes.findIndex(
        (item) => item.key === node.key
      );

      if (localIndex >= 0) {
        rotateToIndex(localIndex);
        return;
      }
    }

    pendingFocusKeyRef.current = node.key;
    setPath(parentPath);
    navigate(getPathUrl(playlistId, parentPath));
  };

  const enterNodeFromPath = (
    node: TCourseTreeNode,
    parentPath: TCourseTreeNode[]
  ) => {
    logCarouselDebug('enterNode', {
      childCount: node.children.length,
      key: node.key,
      title: node.title,
      type: node.type,
    });

    if (node.type === 'placeholder') return;

    if (node.type === 'material') {
      if (!node.itemRef || node.itemRef.item.locked) return;
      navigate(getMaterialUrl(playlistId, parentPath, node.itemRef.index));
      return;
    }

    if (isFileNavigator) return;

    const nextPath = [...parentPath, node];
    setPath(nextPath);
    setArmedNodeKey(null);
    navigate(getPathUrl(playlistId, nextPath));
  };

  const enterNode = (node: TCourseTreeNode) => {
    enterNodeFromPath(node, path);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : undefined;
    const canTilt = Boolean(target?.closest('[data-disc-focused="true"]'));

    stopInertia();
    stopSettle();
    dragModeRef.current = 'pending';
    setIsDragging(true);
    startTiltRef.current = tiltRef.current;
    startXRef.current = event.clientX;
    startYRef.current = event.clientY;
    lastMoveXRef.current = event.clientX;
    lastMoveYRef.current = event.clientY;
    lastMoveTimeRef.current = performance.now();
    tiltAllowedRef.current = canTilt;
    totalDragRef.current = 0;
    velocityRef.current = 0;
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const totalDeltaX = event.clientX - startXRef.current;
    const totalDeltaY = event.clientY - startYRef.current;
    const totalDistance = Math.abs(totalDeltaX) + Math.abs(totalDeltaY);

    if (
      dragModeRef.current === 'pending' &&
      totalDistance >= TILT_GESTURE_THRESHOLD
    ) {
      const absX = Math.abs(totalDeltaX);
      const absY = Math.abs(totalDeltaY);
      dragModeRef.current = metrics.isPortraitPhone
        ? tiltAllowedRef.current && absX > absY * 1.2
          ? 'tilt'
          : 'rotate'
        : tiltAllowedRef.current && absY > absX * 1.2
          ? 'tilt'
          : 'rotate';
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    }

    if (dragModeRef.current === 'tilt') {
      totalDragRef.current = totalDistance;
      velocityRef.current = 0;
      setTiltValue(
        startTiltRef.current -
          (metrics.isPortraitPhone ? totalDeltaX : totalDeltaY) *
            TILT_DEGREES_PER_PIXEL
      );
      return;
    }

    if (dragModeRef.current === 'pending') return;
    setArmedNodeKey(null);

    const now = performance.now();
    const deltaDrag = metrics.isPortraitPhone
      ? lastMoveYRef.current - event.clientY
      : event.clientX - lastMoveXRef.current;
    const deltaTime = Math.max(now - lastMoveTimeRef.current, 1);
    const rawDeltaRotation = (deltaDrag / metrics.radius) * RAD_TO_DEG;
    const resistance = getSeamResistance(
      rotationRef.current + rawDeltaRotation,
      itemCount,
      angleStep
    );
    const deltaRotation = rawDeltaRotation * resistance;

    totalDragRef.current += Math.abs(deltaDrag);
    velocityRef.current = deltaRotation / deltaTime;
    lastMoveXRef.current = event.clientX;
    lastMoveYRef.current = event.clientY;
    lastMoveTimeRef.current = now;
    setRotationValue(rotationRef.current + deltaRotation);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);

    const mode = dragModeRef.current;
    dragModeRef.current = 'pending';

    if (totalDragRef.current > 7) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }

    const seamAnchor = getNearestSeamAnchor(
      rotationRef.current,
      itemCount,
      angleStep
    );
    if (
      mode === 'rotate' &&
      seamAnchor &&
      seamAnchor.distance < SEAM_SNAP_RANGE &&
      Math.abs(velocityRef.current) < SEAM_PASS_SPEED
    ) {
      setRotationValue(seamAnchor.rotation);
      velocityRef.current = 0;
      return;
    }

    if (mode === 'rotate') {
      if (Math.abs(velocityRef.current) >= INERTIA_STOP_SPEED) {
        startInertia();
      } else {
        settleToNearestSlot();
      }
    }
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!displayNodes.length) return;

    event.preventDefault();
    setArmedNodeKey(null);
    stopInertia();
    stopSettle();
    const direction =
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
    const rawDeltaRotation =
      -(direction / metrics.radius) * RAD_TO_DEG * WHEEL_ROTATION_SCALE;
    const resistance = getSeamResistance(
      rotationRef.current + rawDeltaRotation,
      itemCount,
      angleStep
    );
    setRotationValue(rotationRef.current + rawDeltaRotation * resistance);
    wheelSettleTimerRef.current = window.setTimeout(() => {
      wheelSettleTimerRef.current = null;
      settleToNearestSlot();
    }, 160);
  };

  const rotateRealNodeByStep = (stepDelta: number) => {
    const realIndexes = displayNodes.reduce<number[]>((acc, node, index) => {
      const isAvailableFile =
        node.type === 'material' && !node.locked && !node.itemRef?.item.locked;
      const isNavigableNode =
        node.type !== 'placeholder' && (!isFileNavigator || isAvailableFile);

      if (isNavigableNode) acc.push(index);
      return acc;
    }, []);

    if (!realIndexes.length) return;

    const currentRealIndex = realIndexes.reduce((bestIndex, realIndex) => {
      const bestDistance = Math.abs(bestIndex - focusedIndex);
      const nextDistance = Math.abs(realIndex - focusedIndex);
      return nextDistance < bestDistance ? realIndex : bestIndex;
    }, realIndexes[0]);
    const currentOrdinal = realIndexes.indexOf(currentRealIndex);
    const nextOrdinal =
      (currentOrdinal - stepDelta + realIndexes.length) % realIndexes.length;

    setArmedNodeKey(null);
    rotateToIndex(realIndexes[nextOrdinal]);
  };

  const navigateLevelByStep = (stepDelta: number) => {
    if (isFileNavigator) return;

    if (stepDelta < 0) {
      if (!path.length) return;

      const nextPath = path.slice(0, -1);
      setPath(nextPath);
      setArmedNodeKey(null);
      navigate(getPathUrl(playlistId, nextPath));
      return;
    }

    const nodeToEnter =
      focusedNode?.type !== 'placeholder'
        ? focusedNode
        : displayNodes.find((node) => node.type !== 'placeholder');

    if (
      !nodeToEnter ||
      nodeToEnter.type === 'material' ||
      !nodeToEnter.children.length
    ) {
      return;
    }

    enterNode(nodeToEnter);
  };

  const isTesseractVertexHit = (
    event: PointerEvent<HTMLElement>,
    target: HTMLElement
  ): boolean => {
    const rect = target.getBoundingClientRect();
    const pointer = getPointerInTesseractFigure(event, target);
    const points = getProjectedTesseractPoints(
      tesseractBaseRotation.item + tesseractPreview.item,
      tesseractBaseRotation.level + tesseractPreview.level,
      tesseractFreeRotation
    );
    const figureSize = getTesseractFigureSize(rect);
    const hitRadius = clamp(
      figureSize * TESSERACT_VERTEX_HIT_RATIO,
      TESSERACT_VERTEX_HIT_MIN,
      TESSERACT_VERTEX_HIT_MAX
    );

    const nearestOuterVertices = points
      .map((point, index) => ({
        index,
        point,
      }))
      .filter(({ index }) => TESSERACT_VERTICES[index][3] > 0)
      .sort((first, second) => second.point.depth - first.point.depth)
      .slice(0, TESSERACT_3D_HANDLE_VERTEX_COUNT);

    return nearestOuterVertices.some(({ point }) => {
      const distance =
        (Math.hypot(pointer.x - point.x, pointer.y - point.y) / 100) *
        figureSize;

      return distance <= hitRadius;
    });
  };

  const getTesseractHitFace = (
    event: PointerEvent<HTMLElement>,
    target: HTMLElement
  ): TTesseractFace | null => {
    const pointer = getPointerInTesseractFigure(event, target);
    const points = getProjectedTesseractPoints(
      tesseractBaseRotation.item + tesseractPreview.item,
      tesseractBaseRotation.level + tesseractPreview.level,
      tesseractFreeRotation
    );
    const projectedFaces = projectTesseractFaces(
      points,
      TESSERACT_SELECTABLE_HANDLE_FACES
    ).sort((first, second) => second.center.depth - first.center.depth);

    return (
      projectedFaces.find(({ points: facePoints }) => {
        return (
          isPointInPolygon(pointer, facePoints) ||
          getDistanceToPolygon(pointer, facePoints) <=
            TESSERACT_FACE_HIT_PADDING
        );
      })?.face ?? null
    );
  };

  const handleTesseractPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    stopInertia();
    stopSettle();
    const startsOnVertex = isTesseractVertexHit(event, event.currentTarget);
    const hitFace = startsOnVertex
      ? null
      : getTesseractHitFace(event, event.currentTarget);
    const startsOnFace = Boolean(hitFace);

    if (!startsOnVertex && !startsOnFace) return;

    stopTesseractAutoRotation();
    setTesseractHolding(true);

    if (hitFace) {
      setTesseractSelectedFaceKey(hitFace.key);
    }

    const dragState: NonNullable<TTesseractDragState> = {
      faceKey: hitFace?.key,
      freeStartX: tesseractFreeRotation.x,
      freeStartY: tesseractFreeRotation.y,
      handleKind: startsOnVertex ? 'vertex3d' : 'face4d',
      lastStep: 0,
      lastX: event.clientX,
      lastY: event.clientY,
      mode: startsOnVertex ? 'free' : 'idle',
      previewItem: 0,
      previewLevel: 0,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };

    tesseractDragRef.current = dragState;
    setTesseractPreview({ item: 0, level: 0 });
    setTesseractActiveMode(startsOnVertex ? 'free' : 'idle');
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTesseractPointerMove = (event: PointerEvent<HTMLElement>) => {
    const dragState = tesseractDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    dragState.lastX = event.clientX;
    dragState.lastY = event.clientY;

    const horizontalAxis = event.clientX - dragState.startX;
    const verticalAxis = dragState.startY - event.clientY;
    const horizontalDistance = Math.abs(horizontalAxis);
    const verticalDistance = Math.abs(verticalAxis);

    if (dragState.handleKind === 'vertex3d' && dragState.mode === 'free') {
      setTesseractFreeRotation({
        x: clamp(
          dragState.freeStartX + (event.clientY - dragState.startY) * 0.35,
          -42,
          42
        ),
        y: clamp(
          dragState.freeStartY + (event.clientX - dragState.startX) * 0.35,
          -42,
          42
        ),
      });
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (dragState.handleKind !== 'face4d') return;

    const selectedFace = getTesseractSelectableFaceByKey(dragState.faceKey);
    const isRightFace = selectedFace.key === TESSERACT_OUTER_RIGHT_FACE.key;
    const dominantAxis =
      verticalDistance >= horizontalDistance ? verticalAxis : horizontalAxis;
    const itemAxis = verticalAxis;
    const levelAxis = isRightFace ? dominantAxis : horizontalAxis;

    if (
      dragState.mode === 'idle' &&
      Math.max(verticalDistance, horizontalDistance) >=
        TESSERACT_GESTURE_THRESHOLD
    ) {
      dragState.mode =
        isRightFace || horizontalDistance > verticalDistance
          ? 'levels'
          : 'items';
      dragState.lastStep = 0;
      setTesseractActiveMode(dragState.mode);
    }

    const nextPreview = {
      item: dragState.mode === 'items' ? itemAxis * 0.86 : 0,
      level: dragState.mode === 'levels' ? levelAxis * 0.72 : 0,
    };

    dragState.previewItem = nextPreview.item;
    dragState.previewLevel = nextPreview.level;
    setTesseractPreview(nextPreview);

    if (dragState.mode === 'items') {
      const nextStep = Math.trunc(itemAxis / TESSERACT_ITEM_STEP_PX);
      const stepDelta = nextStep - dragState.lastStep;

      if (stepDelta !== 0) {
        rotateRealNodeByStep(stepDelta);
        dragState.lastStep = nextStep;
      }
    }

    if (dragState.mode === 'levels') {
      const nextStep = Math.trunc(levelAxis / TESSERACT_LEVEL_STEP_PX);
      const stepDelta = nextStep - dragState.lastStep;

      if (stepDelta !== 0) {
        const direction = stepDelta > 0 ? 1 : -1;
        navigateLevelByStep(direction);
        dragState.lastStep += direction;
      }
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const handleTesseractPointerUp = (event: PointerEvent<HTMLElement>) => {
    const dragState = tesseractDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    tesseractDragRef.current = null;
    setTesseractHolding(false);
    if (dragState.handleKind === 'face4d') {
      setTesseractBaseRotation((current) => ({
        item: normalizeTesseractAngle(current.item + dragState.previewItem),
        level: normalizeTesseractAngle(current.level + dragState.previewLevel),
      }));
    }
    setTesseractActiveMode('idle');
    setTesseractPreview({ item: 0, level: 0 });
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerInside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    setManipulatorHover(pointerInside);
    event.preventDefault();
    event.stopPropagation();
  };

  const activeTitle = activeParent?.title ?? 'Course modules';
  const activeCompleted = activeNodes.reduce(
    (sum, node) => sum + node.completedCount,
    0
  );
  const activeTotal = activeNodes.reduce(
    (sum, node) => sum + node.totalCount,
    0
  );
  const isTreeVisible = isMobilePanelLayout
    ? treePinned
    : treePinned ||
      (allowImplicitPanelReveal && (menuHoverTreeOpen || treeHover));
  const isManipulatorVisible = isMobilePanelLayout
    ? manipulatorPinned
    : manipulatorPinned ||
      (allowImplicitPanelReveal && (menuHoverTreeOpen || manipulatorHover));
  useEffect(() => {
    if (!isManipulatorVisible || tesseractHolding) {
      stopTesseractAutoRotation();
      return undefined;
    }

    const tick = (time: number) => {
      const lastTime = tesseractLastAutoFrameAtRef.current ?? time;
      const elapsed = Math.min(time - lastTime, 50);
      tesseractLastAutoFrameAtRef.current = time;

      setTesseractBaseRotation((current) => ({
        item: normalizeTesseractAngle(
          current.item + elapsed * TESSERACT_DEFAULT_ROTATION_DEGREES_PER_MS
        ),
        level: current.level,
      }));

      tesseractAutoFrameRef.current = requestAnimationFrame(tick);
    };

    tesseractAutoFrameRef.current = requestAnimationFrame(tick);

    return stopTesseractAutoRotation;
  }, [isManipulatorVisible, stopTesseractAutoRotation, tesseractHolding]);

  const tesseractItemAngle = tesseractBaseRotation.item + tesseractPreview.item;
  const tesseractLevelAngle =
    tesseractBaseRotation.level + tesseractPreview.level;
  useEffect(() => {
    const wasVisible = manipulatorWasVisibleRef.current;
    manipulatorWasVisibleRef.current = isManipulatorVisible;

    if (!isManipulatorVisible || wasVisible) return;

    tesseractDragRef.current = null;
    setTesseractActiveMode('idle');
    setTesseractHolding(false);
    setTesseractBaseRotation({ item: 0, level: 0 });
    setTesseractPreview({ item: 0, level: 0 });
    setTesseractSelectedFaceKey(TESSERACT_OUTER_FRONT_FACE.key);
    setTesseractFreeRotation({
      x: TESSERACT_DEFAULT_FREE_ROTATION_X,
      y: TESSERACT_DEFAULT_FREE_ROTATION_Y,
    });
  }, [isManipulatorVisible]);
  useEffect(() => {
    if (!isTreeVisible) {
      if (scrollAutoTargetRef.current === treeScrollRef.current) {
        stopAutoScroll();
      }
      stopScrollInertia();
      return undefined;
    }

    const handleTreeAutoScroll = (event: globalThis.PointerEvent) => {
      const panel = treePanelRef.current;
      const scrollArea = treeScrollRef.current;
      if (!panel || !scrollArea || scrollDragRef.current) return;

      const rect = panel.getBoundingClientRect();
      const insideHorizontalBand =
        event.clientX >= rect.left - 70 && event.clientX <= rect.right + 70;

      if (!insideHorizontalBand) {
        if (scrollAutoTargetRef.current === scrollArea) {
          stopAutoScroll();
        }
        return;
      }

      updateAutoScrollFromPointer(scrollArea, event.clientY);
    };

    window.addEventListener('pointermove', handleTreeAutoScroll);

    return () => {
      window.removeEventListener('pointermove', handleTreeAutoScroll);
    };
  }, [
    isTreeVisible,
    stopAutoScroll,
    stopScrollInertia,
    updateAutoScrollFromPointer,
  ]);

  const handleToggleTree = () => {
    if (isMobilePanelLayout) {
      setMenuHoverTreeOpen(false);
      setTreeHover(false);
      setTreePinned((current) => !current);
      return;
    }

    menuHoverSuppressedRef.current = true;
    setMenuHoverTreeOpen(false);
    setTreePinned((current) => {
      const nextPinned = !current;
      if (!nextPinned) {
        setTreeHover(false);
      }
      return nextPinned;
    });
  };

  const handleToggleManipulator = () => {
    if (isMobilePanelLayout) {
      setMenuHoverTreeOpen(false);
      setManipulatorHover(false);
      setManipulatorPinned((current) => !current);
      return;
    }

    menuHoverSuppressedRef.current = true;
    setMenuHoverTreeOpen(false);
    setManipulatorPinned((current) => {
      const nextPinned = !current;
      if (!nextPinned) {
        setManipulatorHover(false);
      }
      return nextPinned;
    });
  };
  const treeRootNodes = tree;
  const treeRootPath: TCourseTreeNode[] = [];

  const renderTreeNode = (
    node: TCourseTreeNode,
    nodePath: TCourseTreeNode[]
  ) => {
    const isFocusedTreeNode = node.key === focusedNodeKey;
    const isActiveTreeNode = activePathKeys.has(node.key) || isFocusedTreeNode;
    const isCompletedTreeNode = Boolean(node.isFullyCompleted);
    const isLocked = Boolean(node.locked);
    const nextPath = [...nodePath, node];

    return (
      <TreeBranch key={node.key}>
        <TreeNodeButton
          type="button"
          data-tree-node-button="true"
          $active={isActiveTreeNode}
          $completed={isCompletedTreeNode}
          $focused={isFocusedTreeNode}
          $material={node.type === 'material'}
          $locked={isLocked}
          disabled={isLocked && node.type === 'material'}
          aria-current={isFocusedTreeNode ? 'true' : undefined}
          onClick={() => {
            if (suppressClickRef.current) return;

            focusNodeFromTree(node, nodePath);
          }}
          onDoubleClick={(event) => {
            event.preventDefault();
            if (suppressClickRef.current) return;

            pendingFocusKeyRef.current = null;
            enterNodeFromPath(node, nodePath);
          }}
        >
          <TreeDot $active={isActiveTreeNode} $focused={isFocusedTreeNode} />
          <TreeText>
            <TreeKind>{getTreeLevelLabel(node)}</TreeKind>
            <TreeName>{node.title}</TreeName>
          </TreeText>
        </TreeNodeButton>
        {node.children.length > 0 && (
          <TreeList>
            {node.children.map((child) => renderTreeNode(child, nextPath))}
          </TreeList>
        )}
      </TreeBranch>
    );
  };

  return (
    <Wrapper $navigatorOnly={isFileNavigator}>
      <TreeDockButton
        type="button"
        $active={isTreeVisible}
        $slotIndex={0}
        aria-label={
          treePinned ? 'Hide course structure' : 'Show course structure'
        }
        aria-pressed={treePinned}
        onClick={handleToggleTree}
      >
        <ApartmentOutlined />
      </TreeDockButton>
      <TreeDockButton
        type="button"
        $active={isManipulatorVisible}
        $slotIndex={1}
        aria-label={
          manipulatorPinned
            ? 'Hide tesseract navigator'
            : 'Show tesseract navigator'
        }
        aria-pressed={manipulatorPinned}
        onClick={handleToggleManipulator}
      >
        <DeploymentUnitOutlined />
      </TreeDockButton>
      {!isFileNavigator && (
        <CarouselNavRow>
          <TopicHeader>
            <TopicTitle>{activeTitle}</TopicTitle>
            <TopicProgress>
              {activeCompleted} / {activeTotal} completed
            </TopicProgress>
          </TopicHeader>
          <PillsRow>
            <TopicPill
              type="button"
              $active={path.length === 0}
              $completed={tree.every((node) => node.isFullyCompleted)}
              aria-pressed={path.length === 0}
              onClick={() => {
                setPath([]);
                navigate(getPlayCourseModulesPath(playlistId));
              }}
            >
              Modules
            </TopicPill>
            {path.map((node, index) => (
              <TopicPill
                key={node.key}
                type="button"
                $active={index === path.length - 1}
                $completed={node.isFullyCompleted}
                aria-pressed={index === path.length - 1}
                onClick={() => {
                  const nextPath = path.slice(0, index + 1);
                  setPath(nextPath);
                  navigate(getPathUrl(playlistId, nextPath));
                }}
              >
                {node.isFullyCompleted && <PillCheck />}
                {node.title}
              </TopicPill>
            ))}
          </PillsRow>
        </CarouselNavRow>
      )}
      <CarouselFrame $navigatorOnly={isFileNavigator}>
        {!isFileNavigator && <SpatialBackdrop />}
        <StructureTree
          ref={treePanelRef}
          $cardHeight={metrics.cardHeight}
          $cardWidth={metrics.cardWidth}
          $visible={isTreeVisible}
          aria-label="Course structure"
          data-carousel-panel="true"
          onPointerEnter={() => setTreeHover(true)}
          onPointerLeave={() => setTreeHover(false)}
        >
          <TreeScrollArea
            ref={treeScrollRef}
            onPointerDown={handleScrollablePointerDown}
            onPointerMove={handleScrollablePointerMove}
            onPointerUp={handleScrollablePointerUp}
            onPointerCancel={handleScrollablePointerUp}
            onPointerLeave={(event) => {
              if (scrollDragRef.current?.pointerId !== event.pointerId) {
                updateAutoScrollFromPointer(event.currentTarget, event.clientY);
              }
            }}
          >
            <TreeContent>
              <TreeTitle>
                {isFileNavigator ? 'Files' : 'Course structure'}
              </TreeTitle>
              <TreeList $root>
                {treeRootNodes.map((node) =>
                  renderTreeNode(node, treeRootPath)
                )}
              </TreeList>
            </TreeContent>
          </TreeScrollArea>
        </StructureTree>
        <TesseractPanel
          $activeMode={tesseractActiveMode}
          $cardHeight={metrics.cardHeight}
          $cardWidth={metrics.cardWidth}
          $visible={isManipulatorVisible}
          aria-label="Tesseract carousel navigator"
          data-carousel-panel="true"
          onPointerCancel={handleTesseractPointerUp}
          onPointerDown={handleTesseractPointerDown}
          onPointerEnter={() => setManipulatorHover(true)}
          onPointerLeave={() => {
            if (!tesseractDragRef.current) {
              setManipulatorHover(false);
            }
          }}
          onPointerMove={handleTesseractPointerMove}
          onPointerUp={handleTesseractPointerUp}
        >
          <TesseractAxisHint $active={tesseractActiveMode === 'levels'} />
          <TesseractManipulator
            activeMode={tesseractActiveMode}
            freeRotation={tesseractFreeRotation}
            itemAngle={tesseractItemAngle}
            levelAngle={tesseractLevelAngle}
            selectedFaceKey={tesseractSelectedFaceKey}
          />
        </TesseractPanel>
        {!isFileNavigator && (
          <DiscViewport
            $sceneHeight={metrics.sceneHeight}
            onPointerCancel={handlePointerUp}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onWheel={handleWheel}
          >
            <DiscScene>
              <DiscSurface
                $cardHeight={metrics.cardHeight}
                $isPortraitPhone={metrics.isPortraitPhone}
                $placement="top"
                $surfaceHeight={metrics.surfaceHeight}
                $surfaceWidth={metrics.surfaceWidth}
                $tilt={tilt}
              />
              <DiscSurface
                $cardHeight={metrics.cardHeight}
                $isPortraitPhone={metrics.isPortraitPhone}
                $placement="bottom"
                $surfaceHeight={metrics.surfaceHeight}
                $surfaceWidth={metrics.surfaceWidth}
                $tilt={tilt}
              />
              <DiscStage
                $isPortraitPhone={metrics.isPortraitPhone}
                $radius={metrics.radius}
                $tilt={tilt}
              >
                {displayNodes.map((node, localIndex) => {
                  const status = getCardStatus(node);
                  const displayName = node.title;
                  const orderNo = node.itemRef?.item.order ?? localIndex + 1;
                  const shouldRenderDetails =
                    localIndex === focusedIndex && status !== 'placeholder';
                  const metadata = shouldRenderDetails
                    ? getModule1Metadata(node)
                    : {};
                  const description = metadata.description?.trim();
                  const benefits = normalizeBenefit(metadata.benefit);
                  const materialChildren =
                    shouldRenderDetails && isMaterialLeafGroup(node)
                      ? node.children.filter((child) => child.itemRef)
                      : [];
                  const materialParentPath = [...path, node];
                  const hasDetails =
                    shouldRenderDetails &&
                    Boolean(
                      description || benefits.length || materialChildren.length
                    );
                  const angle = localIndex * angleStep + rotation;
                  const frontAngle = getLoopedAngle(angle, cycleAngle);
                  const angleRad = (frontAngle * Math.PI) / 180;
                  const frontness = Math.max(0, Math.cos(angleRad));
                  const isFocused = localIndex === focusedIndex;
                  const detailLevel: TCardDetailLevel = isFocused
                    ? 'focused'
                    : metrics.isPortraitPhone &&
                        Math.abs(frontAngle) <= MOBILE_HIGH_DETAIL_EDGE_ANGLE
                      ? 'near'
                      : 'far';
                  const isVisible =
                    frontness > 0 &&
                    Math.abs(frontAngle) <=
                      VISIBLE_EDGE_ANGLE + SLOT_ANGLE * 0.6;

                  if (!isVisible) {
                    return null;
                  }

                  const showBars =
                    detailLevel === 'far' || node.type === 'placeholder';
                  const sideBars = showBars
                    ? sideBarsByKey.get(node.key)
                    : undefined;
                  const kindIcon =
                    node.type !== 'material' ? (
                      <FolderOutlined />
                    ) : node.itemRef?.item.type === 'video' ? (
                      <VideoCameraOutlined />
                    ) : (
                      <FileOutlined />
                    );

                  return (
                    <SpineCard
                      key={node.key}
                      type="button"
                      $angle={frontAngle}
                      $cardHeight={metrics.cardHeight}
                      $cardWidth={metrics.cardWidth}
                      $detailLevel={detailLevel}
                      $focused={isFocused}
                      $frontness={frontness}
                      $isPortraitPhone={metrics.isPortraitPhone}
                      $radius={metrics.radius}
                      $status={status}
                      $visible={isVisible}
                      title={getAvailabilityText(status)}
                      aria-label={`${orderNo}. ${displayName} — ${
                        getStatusCaption(status) || 'Available'
                      }`}
                      aria-disabled={status === 'locked'}
                      data-disc-focused={isFocused ? 'true' : undefined}
                      onClick={(event) => {
                        logCarouselDebug('cardClick', {
                          armedNodeKey,
                          childCount: node.children.length,
                          isFocused,
                          key: node.key,
                          localIndex,
                          status,
                          title: node.title,
                          type: node.type,
                        });

                        if (suppressClickRef.current) {
                          event.preventDefault();
                          return;
                        }
                        if (node.type === 'placeholder') {
                          event.preventDefault();
                          return;
                        }
                        if (status === 'locked') {
                          event.preventDefault();
                          return;
                        }
                        if (!isFocused && armedNodeKey !== node.key) {
                          event.preventDefault();
                          rotateToIndex(localIndex);
                          setArmedNodeKey(node.key);
                          return;
                        }
                        enterNode(node);
                      }}
                    >
                      {showBars ? (
                        <BarsBody>
                          <CardHeader>
                            <KindIcon>{kindIcon}</KindIcon>
                            <OrderNo>{orderNo}</OrderNo>
                            <Name>{displayName}</Name>
                          </CardHeader>
                          <BarFiller>
                            {(sideBars?.description.length ?? 0) > 0 && (
                              <BarSection>
                                <LabelBar />
                                <BarParagraph>
                                  {(sideBars?.description ?? []).map(
                                    (width, barIndex) => (
                                      <AccentBar
                                        key={barIndex}
                                        $width={width}
                                      />
                                    )
                                  )}
                                </BarParagraph>
                              </BarSection>
                            )}
                            {(sideBars?.benefits.length ?? 0) > 0 && (
                              <BarSection>
                                <LabelBar />
                                {(sideBars?.benefits ?? []).map(
                                  (row, rowIndex) => (
                                    <BarRow key={rowIndex}>
                                      <AccentDot />
                                      {row.map((width, barIndex) => (
                                        <AccentBar
                                          key={barIndex}
                                          $width={width}
                                        />
                                      ))}
                                    </BarRow>
                                  )
                                )}
                              </BarSection>
                            )}
                            {(sideBars?.materials.length ?? 0) > 0 && (
                              <BarSection>
                                <LabelBar />
                                {(sideBars?.materials ?? []).map(
                                  (row, rowIndex) => (
                                    <BarRow key={rowIndex}>
                                      <RowDot />
                                      {row.map((width, barIndex) => (
                                        <WordBar
                                          key={barIndex}
                                          $width={width}
                                        />
                                      ))}
                                    </BarRow>
                                  )
                                )}
                              </BarSection>
                            )}
                          </BarFiller>
                          <StatusCaption>
                            {node.type === 'placeholder'
                              ? getStatusCaption(status)
                              : node.type === 'material'
                                ? getStatusCaption(status)
                                : `${node.completedCount} / ${node.totalCount} completed`}
                          </StatusCaption>
                        </BarsBody>
                      ) : (
                        <CardBody>
                          <CardHeader>
                            <KindIcon>{kindIcon}</KindIcon>
                            <OrderNo>{orderNo}</OrderNo>
                            <Name>{displayName}</Name>
                          </CardHeader>
                          {hasDetails && (
                            <CardDetails
                              onPointerDown={handleScrollablePointerDown}
                              onPointerMove={handleScrollablePointerMove}
                              onPointerUp={handleScrollablePointerUp}
                              onPointerCancel={handleScrollablePointerUp}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (suppressClickRef.current) return;

                                const now = performance.now();
                                const previous = lastDetailTapRef.current;
                                lastDetailTapRef.current = {
                                  key: node.key,
                                  time: now,
                                };

                                if (
                                  previous?.key === node.key &&
                                  now - previous.time < 360
                                ) {
                                  enterNode(node);
                                }
                              }}
                              onPointerLeave={(event) => {
                                if (
                                  scrollDragRef.current?.pointerId !==
                                  event.pointerId
                                ) {
                                  updateAutoScrollFromPointer(
                                    event.currentTarget,
                                    event.clientY
                                  );
                                }
                              }}
                            >
                              {description && (
                                <CardDetailBlock>
                                  <CardDetailLabel>Description</CardDetailLabel>
                                  <CardDetailText>{description}</CardDetailText>
                                </CardDetailBlock>
                              )}
                              {benefits.length > 0 && (
                                <CardDetailBlock>
                                  <CardDetailLabel>Benefit</CardDetailLabel>
                                  <CardBenefitList>
                                    {benefits.map((benefit, index) => (
                                      <li key={`${benefit}-${index}`}>
                                        {benefit}
                                      </li>
                                    ))}
                                  </CardBenefitList>
                                </CardDetailBlock>
                              )}
                              {materialChildren.length > 0 && (
                                <CardDetailBlock>
                                  <CardDetailLabel>Materials</CardDetailLabel>
                                  <CardMaterialList>
                                    {materialChildren.map((child) => {
                                      const childLocked = Boolean(child.locked);

                                      return (
                                        <CardMaterialRow
                                          key={child.key}
                                          role="button"
                                          tabIndex={childLocked ? -1 : 0}
                                          $completed={child.isFullyCompleted}
                                          $locked={childLocked}
                                          aria-disabled={childLocked}
                                          title={
                                            childLocked
                                              ? 'Locked until previous material is completed'
                                              : child.title
                                          }
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (
                                              suppressClickRef.current ||
                                              childLocked ||
                                              !child.itemRef
                                            ) {
                                              return;
                                            }
                                            navigate(
                                              getMaterialUrl(
                                                playlistId,
                                                materialParentPath,
                                                child.itemRef.index
                                              )
                                            );
                                          }}
                                        >
                                          <CardMaterialIcon
                                            $completed={child.isFullyCompleted}
                                          >
                                            {childLocked ? (
                                              <LockOutlined />
                                            ) : child.isFullyCompleted ? (
                                              <CheckCircleOutlined />
                                            ) : child.itemRef?.item.type ===
                                              'video' ? (
                                              <VideoCameraOutlined />
                                            ) : (
                                              <FileOutlined />
                                            )}
                                          </CardMaterialIcon>
                                          <CardMaterialName>
                                            {child.title}
                                          </CardMaterialName>
                                        </CardMaterialRow>
                                      );
                                    })}
                                  </CardMaterialList>
                                </CardDetailBlock>
                              )}
                            </CardDetails>
                          )}
                          <StatusCaption>
                            {node.type === 'placeholder'
                              ? getStatusCaption(status)
                              : node.type === 'material'
                                ? getStatusCaption(status)
                                : `${node.completedCount} / ${node.totalCount} completed`}
                          </StatusCaption>
                        </CardBody>
                      )}
                      {status === 'completed' && (
                        <CheckBadge>
                          <CheckCircleOutlined />
                        </CheckBadge>
                      )}
                      {status === 'locked' && (
                        <LockOverlay>
                          <LockOutlined />
                        </LockOverlay>
                      )}
                    </SpineCard>
                  );
                })}
              </DiscStage>
            </DiscScene>
          </DiscViewport>
        )}
      </CarouselFrame>
    </Wrapper>
  );
};
